import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ProductsService } from 'src/app/_services/products.service';
import { GlobalService } from 'src/app/_services/global.service';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { MultiSelect } from 'primeng/multiselect';
import { MenuItem, ConfirmationService } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

interface VariantOption {
  name: string;
  values: string[];
  draftValue?: string;
  colorDraft?: string;
  colorValueMap?: Record<string, string>;
  optionType?: string;
  isCustomName?: boolean;
  cachedColorOptions?: ColorOption[];
  cachedSizeOptions?: Array<{ label: string; value: string }>;
  cachedMaterialOptions?: Array<{ label: string; value: string }>;
  cachedStyleOptions?: Array<{ label: string; value: string }>;
  cachedTargetGenderOptions?: Array<{ label: string; value: string }>;
  cachedLengthOptions?: Array<{ label: string; value: string }>;
  id?: number | string | null;
  valueIds?: Record<string, number | string>;
}

interface ColorOption {
  name: string;
  hex: string;
  id?: number | string;
}

@Component({
  selector: 'app-product-variants',
  templateUrl: './product-variants.component.html',
  styleUrls: ['./product-variants.component.scss']
})
export class ProductVariantsComponent implements OnInit, OnDestroy {
  @ViewChild('addOptionPanel') addOptionPanel?: OverlayPanel;
  @ViewChild('addOptionSearchInput') addOptionSearchInput?: ElementRef<HTMLInputElement>;
  isShippingDialogVisible = false;
  activeShippingRow: {
    shippingWeight?: number | null;
    shippingHeight?: number | null;
    shippingWidth?: number | null;
    shippingLength?: number | null;
    shippingDimensionUOM?: number | string | null;
  } | null = null;
  // Draft the dialog edits against; the row itself is only written to if the
  // user actually changes a field (shippingDirty), so merely opening/closing
  // the dialog on an un-customized variant does not bake the product-level
  // default into that variant's saved data.
  activeShippingDraft: {
    shippingWeight: number | null;
    shippingHeight: number | null;
    shippingWidth: number | null;
    shippingLength: number | null;
    shippingDimensionUOM: number | string | null;
  } | null = null;
  shippingDirty = false;
  applyShippingToAllVariants = false;
  options: VariantOption[] = [];
  isAddOptionPanelOpen = false;
  isPresetLoading = false;
  presetLoadError = '';
  customOptionError = '';
  highlightedAddOptionIndex = -1;
  private readonly isAndroidOs = this.detectAndroidOs();
  private lastAddOptionActionAt = 0;
  private addOptionPanelTriggerEl: HTMLElement | null = null;
  private readonly maxCustomOptionLength = 80;
  private readonly fallbackOptionSuggestions: string[] = [
    'Color',
    'Size',
    'Material',
    'Style',
    'Gender',
    'Length'
  ];
  private baseOptionSuggestions: string[] = [...this.fallbackOptionSuggestions];
  optionSuggestions: string[] = [...this.baseOptionSuggestions];
  filteredOptionSuggestions: string[] = [];
  addOptionSearch = '';
  filteredAddOptionSuggestions: string[] = [];
  variantRows: Array<{
    id: string;
    options: Record<string, string>;
    price: number | null;
    available: number | null;
    image: string | null;
    sku: string;
    barcode: string;
    skuId?: number | string | null;
    shippingWeight?: number | null;
    shippingHeight?: number | null;
    shippingWidth?: number | null;
    shippingLength?: number | null;
    shippingWeightUOM?: number | string | null;
    shippingDimensionUOM?: number | string | null;
  }> = [];
  filteredVariantRows: Array<{
    id: string;
    options: Record<string, string>;
    price: number | null;
    available: number | null;
    image: string | null;
    sku: string;
    barcode: string;
    skuId?: number | string | null;
    shippingWeight?: number | null;
    shippingHeight?: number | null;
    shippingWidth?: number | null;
    shippingLength?: number | null;
    shippingWeightUOM?: number | string | null;
    shippingDimensionUOM?: number | string | null;
  }> = [];
  groupByOption: string | null = null;
  groupByOptionsList: Array<{ label: string; value: string }> = [];
  groupedVariantsList: Array<{
    key: string;
    label: string;
    rows: Array<{
      id: string;
      options: Record<string, string>;
      price: number | null;
      available: number | null;
      image: string | null;
      sku: string;
      barcode: string;
      skuId?: number | string | null;
      shippingWeight?: number | null;
      shippingHeight?: number | null;
      shippingWidth?: number | null;
      shippingLength?: number | null;
      shippingWeightUOM?: number | string | null;
      shippingDimensionUOM?: number | string | null;
    }>;
  }> = [];
  searchQuery = '';
  showSearch = false;
  selectedVariantIds = new Set<string>();
  private deletedVariantIds = new Set<string>();
  variantActionMenu: MenuItem[] = [];
  bulkEditMode: 'price' | 'quantity' | 'sku' | 'barcode' | null = null;
  bulkEditValue: string | number | null = null;
  showPriceColumn = true;
  showImageColumn = true;
  showSkuColumn = false;
  showBarcodeColumn = false;
  showQuantityColumn = false;
  showShippingColumn = true;
  showActionsColumn = true;
  activeOptionNames: string[] = [];
  collapsedGroups = new Set<string>();
  editingOptionIndex: number | null = null;
  optionNameErrors = new Set<number>();
  optionValueErrors = new Map<number, string>();
  variantSkuRequired = new Set<string>();
  variantSkuDuplicate = new Set<string>();
  variantBarcodeRequired = new Set<string>();
  variantBarcodeDuplicate = new Set<string>();
  private editingValueOriginals = new Map<string, string>();
  private rebuildTimer: ReturnType<typeof setTimeout> | null = null;
  private skipNextValueBlurCommit = false;
  private skipNextDraftBlurCommit = false;
  private readonly maxVariantCombinations = 2000;
  private readonly fallbackColorOptions: ColorOption[] = [
    { name: 'Beige', hex: '#f3e5c0' },
    { name: 'Black', hex: '#111827' },
    { name: 'Blue', hex: '#2563eb' },
    { name: 'Bronze', hex: '#cd7f32' },
    { name: 'Brown', hex: '#92400e' },
    { name: 'Clear', hex: '#e5e7eb' },
    { name: 'Gold', hex: '#f59e0b' },
    { name: 'Gray', hex: '#9ca3af' },
    { name: 'Green', hex: '#16a34a' },
    { name: 'Navy', hex: '#1e3a8a' },
    { name: 'Orange', hex: '#f97316' },
    { name: 'Pink', hex: '#ec4899' },
    { name: 'Purple', hex: '#7c3aed' },
    { name: 'Red', hex: '#dc2626' },
    { name: 'Silver', hex: '#cbd5f5' },
    { name: 'Teal', hex: '#0d9488' },
    { name: 'White', hex: '#ffffff' },
    { name: 'Yellow', hex: '#facc15' }
  ];
  private readonly unknownColorFallbackHex = '#9ca3af';
  private baseColorOptions: ColorOption[] = [...this.fallbackColorOptions];
  private readonly fallbackSizeOptions: string[] = [
    '3XS',
    '2XS',
    'XS',
    'S',
    'M',
    'L',
    'XL',
    '2XL',
    '3XL',
    '4XL',
    '5XL'
  ];
  private baseSizeOptions: string[] = [...this.fallbackSizeOptions];
  private baseSizeOptionItems = this.baseSizeOptions.map((value) => ({
    label: value,
    value
  }));
  private readonly fallbackMaterialOptions: string[] = [
    'Acrylic',
    'Angora',
    'Bamboo',
    'Canvas',
    'Cashmere',
    'Corduroy',
    'Cotton',
    'Denim',
    'Faux fur',
    'Fleece',
    'Leather',
    'Linen',
    'Mesh',
    'Nylon',
    'Polyester',
    'Rayon',
    'Satin',
    'Silk',
    'Spandex',
    'Suede',
    'Tweed',
    'Velvet',
    'Wool'
  ];
  private baseMaterialOptions: string[] = [...this.fallbackMaterialOptions];
  private baseMaterialOptionItems = this.baseMaterialOptions.map((value) => ({
    label: value,
    value
  }));
  private readonly fallbackStyleOptions: string[] = [
    'Classic',
    'Casual',
    'Formal',
    'Minimal',
    'Streetwear',
    'Sport',
    'Vintage',
    'Bohemian',
    'Modern',
    'Preppy'
  ];
  private baseStyleOptions: string[] = [...this.fallbackStyleOptions];
  private baseStyleOptionItems = this.baseStyleOptions.map((value) => ({
    label: value,
    value
  }));
  private readonly fallbackTargetGenderOptions: string[] = [
    'Female',
    'Male',
    'Unisex',
    'Other'
  ];
  private readonly standardGenderOrder: Record<string, number> = {
    female: 10,
    woman: 10,
    women: 10,
    male: 20,
    man: 20,
    men: 20,
    unisex: 30,
    other: 40
  };
  private baseTargetGenderOptions: string[] = [...this.fallbackTargetGenderOptions];
  private baseTargetGenderOptionItems = this.baseTargetGenderOptions.map((value) => ({
    label: value,
    value
  }));
  private readonly fallbackLengthOptions: string[] = [
    'Extra short',
    'Short',
    'Regular',
    'Long',
    'Extra long'
  ];
  private baseLengthOptions: string[] = [...this.fallbackLengthOptions];
  private baseLengthOptionItems = this.baseLengthOptions.map((value) => ({
    label: value,
    value
  }));
  private readonly standardOptionTypeOrder = ['color', 'size', 'material', 'style', 'gender', 'length'];
  private readonly standardOptionTypeLabels: Record<string, string> = {
    color: 'Color',
    size: 'Size',
    material: 'Material',
    style: 'Style',
    gender: 'Gender',
    length: 'Length'
  };
  private readonly standardSizeOrder: Record<string, number> = {
    '3xs': 10,
    'xxxs': 10,
    '2xs': 20,
    'xxs': 20,
    'xs': 30,
    's': 40,
    'small': 40,
    'm': 50,
    'medium': 50,
    'l': 60,
    'large': 60,
    'xl': 70,
    '1xl': 70,
    '2xl': 80,
    'xxl': 80,
    '3xl': 90,
    'xxxl': 90,
    '4xl': 100,
    'xxxxl': 100,
    '5xl': 110,
    'xxxxxl': 110
  };
  private presetValueIdMap: Record<string, Record<string, number | string>> = {};
  private presetOptionIdMap: Record<string, number | string> = {};

  constructor(
    public _productService: ProductsService,
    public _abd: AppBaseDataService,
    private _confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.loadVariantOptionPresets();
    this.options = this.normalizeAttributes(this._productService.variantAttributes);
    const hasExistingRows = Array.isArray(this._productService.variantRows) && this._productService.variantRows.length > 0;
    if (hasExistingRows) {
      this.variantRows = this._productService.variantRows.map((row) => ({ ...row }));
    }
    this.syncService();
    if (hasExistingRows) {
      this.initializeVariantRowsFromService();
    } else {
      this.rebuildVariantRows();
    }
    this.buildVariantActionMenu();
  }

  get variantsGridColumns(): string {
    const cols: string[] = [];
    if (this.showSelectionColumn) cols.push('36px');
    cols.push('minmax(180px, 1fr)');
    if (this.showImageColumn) cols.push('108px');
    if (this.showPriceColumn) cols.push('150px');
    if (this.showSkuColumn) cols.push('160px');
    if (this.showBarcodeColumn) cols.push('180px');
    if (this.showQuantityColumn) cols.push('120px');
    if (this.showShippingColumn) cols.push('220px');
    if (this.showRowActionsColumn) cols.push('44px');
    return cols.join(' ');
  }

  get hasWideColumns(): boolean {
    return this.showSkuColumn || this.showBarcodeColumn || this.showShippingColumn;
  }

  ngOnDestroy(): void {
    if (this.rebuildTimer) {
      clearTimeout(this.rebuildTimer);
    }
  }

  get isViewOnly(): boolean {
    return this._productService.isViewOnly;
  }

  get showSelectionColumn(): boolean {
    return !this.isViewOnly;
  }

  get showRowActionsColumn(): boolean {
    return this.showActionsColumn && !this.isViewOnly;
  }

  get useParentSkuBarcodeForVariants(): boolean {
    return this._productService?.useParentSkuBarcodeForVariants === true;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByValueIndex(index: number): number {
    return index;
  }

  addOption(isCustom = false): void {
    if (this.isViewOnly) return;
    const option: VariantOption = {
      name: '',
      values: [],
      optionType: 'other',
      isCustomName: isCustom
    };
    this.updateOptionDefaults(option);
    this.options.push(option);
    this.syncService();
    this.editingOptionIndex = this.options.length - 1;
    this.scheduleRebuild();
  }

  addOptionWithName(name: string, forceCustom = false): void {
    if (this.isViewOnly) return;
    const normalized = this.normalizeName(name);
    if (!normalized || !this.isOptionNameAvailable(normalized)) return;
    const option: VariantOption = {
      name: normalized,
      values: [],
      optionType: this.resolveOptionType(normalized),
      isCustomName: forceCustom || !this.isBaseOptionName(normalized)
    };
    this.updateOptionDefaults(option);
    this.options.push(option);
    this.addOptionSuggestion(normalized);
    this.syncService();
    this.editingOptionIndex = this.options.length - 1;
    this.scheduleRebuild();
  }

  openAddOptionPanel(event: Event): void {
    if (this.isViewOnly) return;
    const trigger = event?.currentTarget as HTMLElement | null;
    this.addOptionPanelTriggerEl = trigger ?? null;
    this.addOptionSearch = '';
    this.customOptionError = '';
    this.highlightedAddOptionIndex = -1;
    this.updateAddOptionSuggestions('');
    this.addOptionPanel?.toggle(event);
  }

  onAddOptionPanelShow(): void {
    this.isAddOptionPanelOpen = true;
    if (this.isAndroidOs) {
      return;
    }
    setTimeout(() => {
      this.addOptionSearchInput?.nativeElement?.focus();
    }, 0);
  }

  onAddOptionPanelHide(): void {
    this.isAddOptionPanelOpen = false;
    this.customOptionError = '';
    this.highlightedAddOptionIndex = -1;
    const trigger = this.addOptionPanelTriggerEl;
    setTimeout(() => {
      try {
        trigger?.focus();
      } catch {}
    }, 0);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !this.isAddOptionPanelOpen) return;
    event.preventDefault();
    event.stopPropagation();
    this.addOptionPanel?.hide();
  }

  onAddOptionSearchChange(value: string): void {
    this.addOptionSearch = value;
    this.customOptionError = '';
    this.updateAddOptionSuggestions(value);
  }

  onAddOptionSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.addOptionPanel?.hide();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.filteredAddOptionSuggestions.length) return;
      this.highlightedAddOptionIndex =
        this.highlightedAddOptionIndex < this.filteredAddOptionSuggestions.length - 1
          ? this.highlightedAddOptionIndex + 1
          : 0;
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.filteredAddOptionSuggestions.length) return;
      this.highlightedAddOptionIndex =
        this.highlightedAddOptionIndex > 0
          ? this.highlightedAddOptionIndex - 1
          : this.filteredAddOptionSuggestions.length - 1;
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (
        this.highlightedAddOptionIndex >= 0 &&
        this.highlightedAddOptionIndex < this.filteredAddOptionSuggestions.length
      ) {
        this.selectSuggestedOption(this.filteredAddOptionSuggestions[this.highlightedAddOptionIndex]);
        return;
      }
      this.createCustomOption();
    }
  }

  selectSuggestedOption(name: string): void {
    if (this.shouldIgnoreDuplicateAddOptionAction()) return;
    this.customOptionError = '';
    this.addOptionWithName(name);
    this.addOptionPanel?.hide();
  }

  createCustomOption(): void {
    if (this.shouldIgnoreDuplicateAddOptionAction()) return;
    if (this.isViewOnly) return;
    const normalized = this.normalizeName(this.addOptionSearch);
    if (!normalized) {
      this.customOptionError = '';
      this.addOption(true);
      this.addOptionPanel?.hide();
      return;
    }
    if (normalized.length > this.maxCustomOptionLength) {
      this.customOptionError = `Option name must be ${this.maxCustomOptionLength} characters or fewer.`;
      return;
    }
    if (!this.isOptionNameAvailable(normalized)) {
      this.customOptionError = 'Option already exists.';
      return;
    }
    this.customOptionError = '';
    this.addOptionWithName(normalized, true);
    this.addOptionPanel?.hide();
  }

  onSuggestedOptionTouchStart(event: TouchEvent, name: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectSuggestedOption(name);
  }

  onCreateCustomOptionTouchStart(event: TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.createCustomOption();
  }

  removeOption(index: number): void {
    if (this.isViewOnly) return;
    this.options.splice(index, 1);
    if (this.editingOptionIndex === index) {
      this.editingOptionIndex = null;
    } else if (this.editingOptionIndex !== null && index < this.editingOptionIndex) {
      this.editingOptionIndex -= 1;
    }
    if (this.optionNameErrors.has(index)) {
      this.optionNameErrors.delete(index);
    }
    this.optionValueErrors.delete(index);
    const shiftedValueErrors = new Map<number, string>();
    this.optionValueErrors.forEach((message, errorIndex) => {
      shiftedValueErrors.set(errorIndex > index ? errorIndex - 1 : errorIndex, message);
    });
    this.optionValueErrors = shiftedValueErrors;
    this.validateAllOptionNames();
    this.syncService();
    this.scheduleRebuild();
  }

  filterOptionSuggestions(event: { query: string }, index?: number): void {
    const rawQuery = (event?.query || '').trim();
    const query = rawQuery.toLowerCase();
    const usedNames = new Set(
      this.options
        .filter((_, idx) => idx !== index)
        .map((opt) => this.normalizeName(opt.name).toLowerCase())
        .filter((name) => !!name)
    );
    const base = this.getActiveOptionSuggestions(index).filter(
      (item) => !usedNames.has(item.toLowerCase())
    );
    if (!query) {
      this.filteredOptionSuggestions = [...base];
      return;
    }
    this.filteredOptionSuggestions = base.filter((item) =>
      item.toLowerCase().includes(query)
    );
  }

  onOptionNameChange(index: number): void {
    const option = this.options[index];
    if (!option) return;
    const previousType = option.optionType || this.resolveOptionType(option.name);
    option.name = this.normalizeName(option.name);
    this.addOptionSuggestion(option.name);
    const nextType = this.resolveOptionType(option.name);
    if (previousType !== nextType) {
      option.values = [];
      option.draftValue = '';
      option.colorDraft = '';
    }
    option.optionType = nextType;
    this.updateOptionDefaults(option);
    this.validateAllOptionNames();
    this.syncService();
    this.scheduleRebuild();
  }

  onOptionNameBlur(index: number): void {
    const option = this.options[index];
    if (!option) return;
    if (!option.isCustomName && option.name && !this.isBaseOptionName(option.name)) {
      option.isCustomName = true;
    }
    this.addOptionSuggestion(option.name);
  }

  applyCustomOption(index: number): void {
    if (this.isViewOnly) return;
    const option = this.options[index];
    if (!option) return;
    const query = this.normalizeName(option.name);
    if (!this.canCreateCustomOption(index)) return;
    option.name = query;
    option.isCustomName = true;
    this.onOptionNameChange(index);
    this.addOptionSuggestion(query);
  }

  onOptionValuesChange(index: number, values?: string[], multiselect?: MultiSelect): void {
    const option = this.options[index];
    if (!option) return;
    if (Array.isArray(values)) {
      option.values = values;
    }
    const normalized = this.normalizeValues(option.values);
    option.values = this.isColorOption(option)
      ? normalized.map((value) => this.toTitleCase(value))
      : this.isTargetGenderOption(option)
        ? this.sortGenderOptions(normalized)
      : normalized;
    if (option.values.length > 0) {
      this.clearOptionValueError(index);
    }
    if (this.isColorOption(option)) {
      const availableColorMap = new Map<string, string>();
      [...(option.cachedColorOptions || []), ...this.baseColorOptions].forEach((entry) => {
        const name = String(entry?.name ?? '').trim();
        const hex = this.normalizeHexValue(entry?.hex);
        if (!name || !hex) return;
        availableColorMap.set(name.toLowerCase(), hex);
      });
      const nextColorValueMap: Record<string, string> = {};
      option.values.forEach((value) => {
        const normalizedValue = String(value ?? '').trim();
        if (!normalizedValue) return;
        const existingHex = this.normalizeHexValue(option.colorValueMap?.[normalizedValue]);
        const dropdownHex = availableColorMap.get(normalizedValue.toLowerCase());
        const resolvedHex = existingHex || dropdownHex;
        if (resolvedHex) {
          nextColorValueMap[normalizedValue] = resolvedHex;
        }
      });
      option.colorValueMap = Object.keys(nextColorValueMap).length ? nextColorValueMap : undefined;
    }
    if (option.valueIds) {
      const nextIds: Record<string, number | string> = {};
      option.values.forEach((value) => {
        if (option.valueIds && option.valueIds[value] !== undefined) {
          nextIds[value] = option.valueIds[value];
        }
      });
      option.valueIds = Object.keys(nextIds).length ? nextIds : undefined;
    }
    this.updateOptionDefaults(option);
    this.syncService();
    this.scheduleRebuild();
    this.keepMultiSelectOpen(multiselect);
  }

  onOptionDrop(event: CdkDragDrop<VariantOption[]>): void {
    if (this.isViewOnly) return;
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.options, event.previousIndex, event.currentIndex);
    this.validateAllOptionNames();
    this.syncService();
    this.scheduleRebuild();
  }

  onValueDrop(event: CdkDragDrop<string[]>, option: VariantOption): void {
    if (this.isViewOnly) return;
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(option.values, event.previousIndex, event.currentIndex);
    this.syncService();
    this.scheduleRebuild();
  }

  private keepMultiSelectOpen(multiselect?: MultiSelect): void {
    if (!multiselect || this.isViewOnly) return;
    setTimeout(() => {
      try {
        multiselect.show();
      } catch { }
    }, 0);
  }

  private getOptionValueEditKey(option: VariantOption, index: number): string {
    const optionKey = String(option?.id ?? option?.name ?? 'option');
    return `${optionKey}:${index}`;
  }

  private isColorPickerElement(target: EventTarget | null | undefined): boolean {
    return (target as HTMLElement | null)?.classList?.contains('color-picker-input') === true;
  }

  onValueEditStart(option: VariantOption, index: number): void {
    const currentValue = String(option?.values?.[index] ?? '').trim();
    this.editingValueOriginals.set(this.getOptionValueEditKey(option, index), currentValue);
  }

  onValueEditCommit(optionIndex: number, option: VariantOption, valueIndex: number, event?: FocusEvent): void {
    const isColorPickerBlur = this.isColorPickerElement(event?.relatedTarget);
    if (!isColorPickerBlur && this.skipNextValueBlurCommit) {
      this.skipNextValueBlurCommit = false;
      return;
    }
    if (isColorPickerBlur) {
      this.skipNextValueBlurCommit = false;
    }

    const key = this.getOptionValueEditKey(option, valueIndex);
    const previousValue = this.editingValueOriginals.get(key) ?? '';
    this.editingValueOriginals.delete(key);

    const rawValue = String(option?.values?.[valueIndex] ?? '').trim();
    if (!rawValue) {
      this.removeValue(option, valueIndex);
      return;
    }

    const nextValue = this.isColorOption(option) ? this.toTitleCase(rawValue) : rawValue;
    const duplicateIndex = option.values.findIndex(
      (value, idx) => idx !== valueIndex && this.normalizeValueKey(value) === this.normalizeValueKey(nextValue)
    );
    if (duplicateIndex >= 0) {
      this.setOptionValueError(optionIndex, `Value "${nextValue}" already exists.`);
      option.values[valueIndex] = previousValue || option.values[valueIndex];
      return;
    }
    option.values[valueIndex] = nextValue;
    this.clearOptionValueError(optionIndex);

    if (this.isColorOption(option)) {
      const nextColorValueMap: Record<string, string> = { ...(option.colorValueMap || {}) };
      const existingHex = previousValue ? nextColorValueMap[previousValue] : undefined;
      const knownHex = this.getKnownColorHex(nextValue);
      if (previousValue && previousValue !== nextValue) {
        delete nextColorValueMap[previousValue];
      }
      if (knownHex) {
        nextColorValueMap[nextValue] = knownHex;
      } else if (existingHex) {
        nextColorValueMap[nextValue] = existingHex;
      }
      option.colorValueMap = Object.keys(nextColorValueMap).length ? nextColorValueMap : undefined;
    }

    if (option.valueIds) {
      const nextValueIds: Record<string, number | string> = {};
      option.values.forEach((value) => {
        const current = option.valueIds?.[value];
        if (current !== undefined) {
          nextValueIds[value] = current;
          return;
        }
        if (previousValue && value === nextValue) {
          const previousId = option.valueIds?.[previousValue];
          if (previousId !== undefined) {
            nextValueIds[value] = previousId;
          }
        }
      });
      option.valueIds = Object.keys(nextValueIds).length ? nextValueIds : undefined;
    }

    this.onOptionValuesChange(optionIndex);
  }

  onValueInputTab(event: KeyboardEvent): void {
    if (event.shiftKey) return;
    const currentInput = event.target as HTMLInputElement | null;
    const container = currentInput?.closest('.variant-fields') as HTMLElement | null;
    if (!currentInput || !container) return;
    const inputs = Array.from(
      container.querySelectorAll<HTMLInputElement>('input.value-input:not([disabled])')
    );
    const currentIndex = inputs.indexOf(currentInput);
    if (currentIndex < 0 || currentIndex >= inputs.length - 1) return;
    event.preventDefault();
    inputs[currentIndex + 1]?.focus();
  }

  prepareExistingColorPick(): void {
    this.skipNextValueBlurCommit = true;
  }

  prepareDraftColorPick(): void {
    this.skipNextDraftBlurCommit = true;
  }

  openColorPicker(input: HTMLInputElement | null, event?: Event): void {
    if (this.isViewOnly || !input) return;
    event?.stopPropagation();

    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }

    try {
      if (typeof pickerInput.showPicker === 'function') {
        pickerInput.showPicker();
        return;
      }
    } catch { }

    input.click();
  }

  onDraftValueBlur(option: VariantOption, event?: FocusEvent): void {
    const isColorPickerBlur = this.isColorPickerElement(event?.relatedTarget);
    if (this.skipNextDraftBlurCommit || isColorPickerBlur) {
      this.skipNextDraftBlurCommit = false;
      return;
    }
    this.addValue(option);
  }

  addValue(option: VariantOption): void {
    if (this.isViewOnly) return;
    const rawInput = option.draftValue || '';
    const parts = rawInput
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    if (!parts.length) return;

    const normalized = this.normalizeValues([...(option.values || []), ...parts]);
    const duplicateCandidate = this.getDuplicateValue([...(option.values || []), ...parts]);
    if (duplicateCandidate) {
      const optionIndex = this.options.indexOf(option);
      if (optionIndex >= 0) {
        this.setOptionValueError(optionIndex, `Value "${duplicateCandidate}" already exists.`);
      }
    }
    option.values = this.isColorOption(option)
      ? normalized.map((value) => this.toTitleCase(value))
      : normalized;
    if (this.isColorOption(option)) {
      const nextColorValueMap: Record<string, string> = { ...(option.colorValueMap || {}) };
      const selectedHex = this.normalizeHexValue(option.colorDraft);
      parts.forEach((part, offset) => {
        const displayValue = this.toTitleCase(String(part ?? '').trim());
        if (!displayValue) return;
        const knownHex = this.getKnownColorHex(displayValue);
        if (knownHex) {
          nextColorValueMap[displayValue] = knownHex;
          return;
        }
        const isLastAdded = offset === parts.length - 1;
        if (isLastAdded && selectedHex) {
          nextColorValueMap[displayValue] = selectedHex;
        }
      });
      option.colorValueMap = Object.keys(nextColorValueMap).length ? nextColorValueMap : undefined;
    }
    if (option.valueIds) {
      const nextIds: Record<string, number | string> = {};
      option.values.forEach((value) => {
        if (option.valueIds && option.valueIds[value] !== undefined) {
          nextIds[value] = option.valueIds[value];
        }
      });
      option.valueIds = Object.keys(nextIds).length ? nextIds : undefined;
    }
    option.draftValue = '';
    option.colorDraft = '';
    const optionIndex = this.options.indexOf(option);
    if (optionIndex >= 0 && option.values.length > 0) {
      if (!duplicateCandidate) {
        this.clearOptionValueError(optionIndex);
      }
    }
    this.updateOptionDefaults(option);
    this.syncService();
    this.scheduleRebuild();
  }

  removeValue(option: VariantOption, index: number): void {
    if (this.isViewOnly) return;
    const removedValue = option.values[index];
    option.values.splice(index, 1);
    if (this.isColorOption(option) && option.colorValueMap && removedValue !== undefined) {
      delete option.colorValueMap[removedValue];
      if (!Object.keys(option.colorValueMap).length) {
        option.colorValueMap = undefined;
      }
    }
    if (option.valueIds) {
      const nextIds: Record<string, number | string> = {};
      option.values.forEach((value) => {
        if (option.valueIds && option.valueIds[value] !== undefined) {
          nextIds[value] = option.valueIds[value];
        }
      });
      option.valueIds = Object.keys(nextIds).length ? nextIds : undefined;
    }
    const optionIndex = this.options.indexOf(option);
    if (optionIndex >= 0) {
      this.clearOptionValueError(optionIndex);
    }
    this.updateOptionDefaults(option);
    this.syncService();
    this.scheduleRebuild();
  }

  onCustomColorPick(option: VariantOption, color: string): void {
    if (this.isViewOnly) return;
    if (!color) return;
    option.colorDraft = color;
  }

  onDraftValueChange(option: VariantOption, value: string): void {
    option.draftValue = value;
    if (!this.isColorOption(option)) return;
    if (!String(value ?? '').trim()) {
      option.colorDraft = '';
      return;
    }
    const knownHex = this.getKnownColorHex(value);
    if (knownHex) {
      option.colorDraft = knownHex;
    }
  }

  getEditableColorValue(option: VariantOption, value: string): string {
    const mapped = this.normalizeHexValue(option?.colorValueMap?.[value]);
    if (mapped) return mapped;
    return this.getValueSwatch(option, value) ?? '#000000';
  }

  onExistingColorPick(option: VariantOption, value: string, color: string): void {
    if (this.isViewOnly) return;
    const normalizedColor = this.normalizeHexValue(color);
    const normalizedValue = String(value ?? '').trim();
    if (!normalizedColor || !normalizedValue) return;
    const nextColorValueMap: Record<string, string> = { ...(option.colorValueMap || {}) };
    nextColorValueMap[normalizedValue] = normalizedColor;
    option.colorValueMap = nextColorValueMap;
    this.updateOptionDefaults(option);
    this.syncService();
  }

  isColorOption(option: VariantOption): boolean {
    const name = (option?.name || '').trim().toLowerCase();
    return name === 'color' || name === 'colour' || name?.includes('color') || name?.includes('colour');
  }

  isSizeOption(option: VariantOption): boolean {
    const name = (option?.name || '').trim().toLowerCase();
    return name === 'size' || name?.includes('size');
  }

  isMaterialOption(option: VariantOption): boolean {
    const name = (option?.name || '').trim().toLowerCase();
    return name === 'material' || name?.includes('material');
  }

  isStyleOption(option: VariantOption): boolean {
    const name = (option?.name || '').trim().toLowerCase();
    return name === 'style' || name?.includes('style');
  }

  isTargetGenderOption(option: VariantOption): boolean {
    const name = (option?.name || '').trim().toLowerCase();
    return name === 'target gender' || name === 'gender' || name?.includes('gender');
  }

  isLengthOption(option: VariantOption): boolean {
    const name = (option?.name || '').trim().toLowerCase();
    return name === 'length' || name?.includes('length');
  }


  getValuesPlaceholder(option: VariantOption): string {
    const base = option?.name?.trim();
    if (!base) return 'Add value';
    return `Add ${base.toLowerCase()}`;
  }

  getAddValuePlaceholder(option: VariantOption): string {
    if (this.isColorOption(option)) {
      return 'Add custom color';
    }
    if (this.isSizeOption(option)) {
      return 'Add custom size';
    }
    if (this.isMaterialOption(option)) {
      return 'Add custom material';
    }
    if (this.isStyleOption(option)) {
      return 'Add custom style';
    }
    if (this.isTargetGenderOption(option)) {
      return 'Add custom gender';
    }
    if (this.isLengthOption(option)) {
      return 'Add custom length';
    }
    return 'Add another value';
  }

  formatSelectedItemsSummary(selectedValues: any[] | null | undefined, fallbackLabel: string): string {
    const values = Array.isArray(selectedValues)
      ? selectedValues.map((value) => String(value ?? '').trim()).filter((value) => value.length > 0)
      : [];
    if (!values.length) return fallbackLabel;
    if (values.length <= 5) return values.join(', ');
    const visibleValues = values.slice(0, 5).join(', ');
    return `${visibleValues} +${values.length - 5} selected`;
  }

  getValueSwatch(option: VariantOption, value: string): string | null {
    if (!this.isColorOption(option)) return null;
    const knownHex = this.getKnownColorHex(value);
    if (knownHex) return knownHex;
    const mapped = this.normalizeHexValue(option?.colorValueMap?.[value]);
    if (mapped) return mapped;
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return null;
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalized)) {
      return normalized;
    }
    return this.unknownColorFallbackHex;
  }

  getValueLeadingSwatch(option: VariantOption, value: string): string | null {
    return this.isColorOption(option) ? this.getValueSwatch(option, value) : null;
  }

  isEditingOption(index: number): boolean {
    return this.editingOptionIndex === index;
  }

  startEditOption(index: number): void {
    if (this.isViewOnly) return;
    this.editingOptionIndex = index;
    this.clearOptionValueError(index);
  }

  finishEditOption(index: number): void {
    if (this.isViewOnly) return;
    this.validateAllOptionNames();
    if (this.optionNameErrors.has(index)) {
      return;
    }
    const option = this.options[index];
    if (option) {
      if (String(option.draftValue ?? '').trim()) {
        this.addValue(option);
      }
      if (!option.values?.length) {
        this.setOptionValueError(index, 'Enter at least one option value.');
        return;
      }
      const duplicateValue = this.getDuplicateValue(option.values);
      if (duplicateValue) {
        this.setOptionValueError(index, `Value "${duplicateValue}" already exists.`);
        return;
      }
      this.clearOptionValueError(index);
      this.addOptionSuggestion(option.name);
    }
    if (this.editingOptionIndex === index) {
      this.editingOptionIndex = null;
    }
  }

  onGroupByChange(): void {
    this.collapsedGroups.clear();
    this.applySearchFilter();
  }

  toggleSearch(): void {
    this.showSearch = !this.showSearch;
    if (!this.showSearch) {
      this.searchQuery = '';
      this.applySearchFilter();
    }
  }

  onSearchChange(value: string): void {
    this.searchQuery = value;
    this.applySearchFilter();
  }

  isGroupCollapsed(key: string): boolean {
    return this.collapsedGroups.has(key);
  }

  toggleGroup(key: string): void {
    if (this.collapsedGroups.has(key)) {
      this.collapsedGroups.delete(key);
    } else {
      this.collapsedGroups.add(key);
    }
  }

  toggleAllGroups(): void {
    const groups = this.groupedVariantsList.map((group) => group.key);
    const hasOpen = groups.some((key) => !this.collapsedGroups.has(key));
    this.collapsedGroups = new Set(hasOpen ? groups : []);
  }

  getGroupTotals(group: { rows: Array<{ price: number | null; available: number | null }> }): { quantity: number } {
    const totalQuantity = group.rows.reduce((sum, row) => sum + (row.available ?? 0), 0);
    return { quantity: totalQuantity };
  }

  getGroupPriceDisplay(group: { rows: Array<{ price: number | null }> }): string {
    const normalizedPrices = group.rows
      .map((row) => this.formatPrice(row.price))
      .filter((price): price is number => price !== null);

    if (!normalizedPrices.length) {
      return '0.00';
    }

    const uniquePrices = Array.from(new Set(normalizedPrices.map((price) => price.toFixed(2))));
    if (uniquePrices.length === 1) {
      return uniquePrices[0];
    }

    const minPrice = Math.min(...normalizedPrices);
    const maxPrice = Math.max(...normalizedPrices);
    return `${minPrice.toFixed(2)} - ${maxPrice.toFixed(2)}`;
  }

  isVariantSelected(row: { id: string }): boolean {
    return this.selectedVariantIds.has(row.id);
  }

  onVariantSelectChange(row: { id: string }, event: Event): void {
    if (this.isViewOnly) return;
    const checked = (event.target as HTMLInputElement)?.checked;
    if (checked) {
      this.selectedVariantIds.add(row.id);
    } else {
      this.selectedVariantIds.delete(row.id);
    }
    this.buildVariantActionMenu();
  }

  areAllVisibleSelected(): boolean {
    if (!this.filteredVariantRows.length) return false;
    return this.filteredVariantRows.every((row) => this.selectedVariantIds.has(row.id));
  }

  onSelectAllChange(event: Event): void {
    if (this.isViewOnly) return;
    const checked = (event.target as HTMLInputElement)?.checked;
    if (!checked) {
      this.filteredVariantRows.forEach((row) => this.selectedVariantIds.delete(row.id));
      this.buildVariantActionMenu();
      return;
    }
    this.filteredVariantRows.forEach((row) => this.selectedVariantIds.add(row.id));
    this.buildVariantActionMenu();
  }

  deleteSelectedVariants(): void {
    if (this.isViewOnly) return;
    if (!this.selectedVariantIds.size) return;
    for (const id of this.selectedVariantIds) {
      this.deletedVariantIds.add(id);
    }
    this.selectedVariantIds.clear();
    this.variantRows = this.variantRows.filter((row) => !this.deletedVariantIds.has(row.id));
    this.pruneUnusedOptionValues();
    this.applySearchFilter();
    this.syncVariantState();
    this.validateVariantCodes();
    this.buildVariantActionMenu();
    this.scheduleRebuild();
  }

  deleteVariant(row: { id: string }): void {
    if (this.isViewOnly) return;
    this.deletedVariantIds.add(row.id);
    this.selectedVariantIds.delete(row.id);
    this.variantRows = this.variantRows.filter((item) => item.id !== row.id);
    this.pruneUnusedOptionValues();
    this.applySearchFilter();
    this.syncVariantState();
    this.validateVariantCodes();
    this.buildVariantActionMenu();
    this.scheduleRebuild();
  }

  setBulkEditMode(mode: 'price' | 'quantity' | 'sku' | 'barcode'): void {
    if (this.isViewOnly) return;
    this.bulkEditMode = mode;
    this.bulkEditValue = null;
  }

  clearBulkEdit(): void {
    this.bulkEditMode = null;
    this.bulkEditValue = null;
  }

  applyBulkEdit(): void {
    if (this.isViewOnly) return;
    if (!this.bulkEditMode || !this.selectedVariantIds.size) return;
    if (this.useParentSkuBarcodeForVariants && (this.bulkEditMode === 'sku' || this.bulkEditMode === 'barcode')) {
      return;
    }
    const ids = this.selectedVariantIds;
    for (const row of this.variantRows) {
      if (!ids.has(row.id)) continue;
      if (this.bulkEditMode === 'price') {
        row.price = this.formatPrice(this.bulkEditValue as string | number | null);
      } else if (this.bulkEditMode === 'quantity') {
        row.available = this.normalizeQuantity(this.bulkEditValue);
      } else if (this.bulkEditMode === 'sku') {
        row.sku = String(this.bulkEditValue ?? '');
      } else if (this.bulkEditMode === 'barcode') {
        row.barcode = String(this.bulkEditValue ?? '');
      }
    }
    if (this.bulkEditMode === 'price') {
      this.syncQuickPriceFromVariant(this.bulkEditValue);
    }
    this.applySearchFilter();
    this.syncVariantState();
    this.validateVariantCodes();
  }

  private buildVariantActionMenu(): void {
    const hasSelection = this.selectedVariantIds.size > 0;
    const menu: MenuItem[] = [
      {
        label: `${this.showPriceColumn ? 'Hide' : 'Show'} price column`,
        icon: this.showPriceColumn ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: () => {
          this.showPriceColumn = !this.showPriceColumn;
          this.buildVariantActionMenu();
        }
      },
      {
        label: `${this.showImageColumn ? 'Hide' : 'Show'} image column`,
        icon: this.showImageColumn ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: () => {
          this.showImageColumn = !this.showImageColumn;
          this.buildVariantActionMenu();
        }
      },
      {
        label: `${this.showSkuColumn ? 'Hide' : 'Show'} SKU column`,
        icon: this.showSkuColumn ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: () => {
          this.showSkuColumn = !this.showSkuColumn;
          this.buildVariantActionMenu();
        }
      },
      {
        label: `${this.showBarcodeColumn ? 'Hide' : 'Show'} barcode column`,
        icon: this.showBarcodeColumn ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: () => {
          this.showBarcodeColumn = !this.showBarcodeColumn;
          this.buildVariantActionMenu();
        }
      },
      {
        label: `${this.showShippingColumn ? 'Hide' : 'Show'} shipping column`,
        icon: this.showShippingColumn ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: () => {
          this.showShippingColumn = !this.showShippingColumn;
          this.buildVariantActionMenu();
        }
      }
    ];

    if (!this.isViewOnly) {
      menu.push(
        { separator: true },
        {
          label: 'Delete variants',
          icon: 'pi pi-trash',
          command: () => this.deleteSelectedVariants(),
          disabled: !hasSelection
        }
      );
    }

    this.variantActionMenu = menu;
  }

  getVariantLabel(row: { options: Record<string, string> }): string {
    if (!row?.options) return '';
    return Object.values(row.options).join(' / ');
  }

  getGroupedRowLabel(row: { options: Record<string, string> }): string {
    if (!row?.options) return '';
    const entries = Object.entries(row.options).filter(
      ([key]) => key !== this.groupByOption
    );
    return entries.map(([, value]) => value).join(' / ');
  }

  onVariantPriceChange(row: { price: number | null }, value: string | number | null): void {
    row.price = this.formatPrice(value);
    this.syncQuickPriceFromVariant(row.price);
    this.syncVariantState();
  }

  onVariantSkuChange(row: { sku: string }, value: string | null): void {
    if (this.useParentSkuBarcodeForVariants) return;
    const sku = String(value ?? '');
    row.sku = sku;
    const target = row as any;
    target.skuCode = sku;
    target.SkuCode = sku;
    this.syncVariantState();
    this.validateVariantCodes();
  }

  onVariantBarcodeChange(row: { barcode: string }, value: string | null): void {
    if (this.useParentSkuBarcodeForVariants) return;
    row.barcode = String(value ?? '');
    this.syncVariantState();
    this.validateVariantCodes();
  }

  isVariantSkuInvalid(rowId: string): boolean {
    return this.variantSkuRequired.has(rowId) || this.variantSkuDuplicate.has(rowId);
  }

  getVariantSkuError(rowId: string): string | null {
    if (this.variantSkuRequired.has(rowId)) return 'SKU is required.';
    if (this.variantSkuDuplicate.has(rowId)) return 'SKU must be unique.';
    return null;
  }

  isVariantBarcodeInvalid(rowId: string): boolean {
    return this.variantBarcodeRequired.has(rowId) || this.variantBarcodeDuplicate.has(rowId);
  }

  getVariantBarcodeError(rowId: string): string | null {
    if (this.variantBarcodeRequired.has(rowId)) return 'Barcode is required.';
    if (this.variantBarcodeDuplicate.has(rowId)) return 'Barcode must be unique.';
    return null;
  }

  isFormValid(): boolean {
    this.validateAllOptionNames();
    return !this.optionNameErrors.size && this.validateVariantCodes();
  }

  onVariantPriceBlur(row: { price: number | null }): void {
    if (row.price === null || row.price === undefined || row.price === ('' as any)) {
      row.price = this.formatPrice(0);
    }
    this.syncQuickPriceFromVariant(row.price);
    this.syncVariantState();
  }

  onVariantPriceFocus(row: { price: number | null }, event: FocusEvent): void {
    const input = event?.target as HTMLInputElement | null;
    if (!input) return;
    const rawValue = (input.value || '').trim();
    if (!this.isZeroDisplayValue(rawValue)) return;
    row.price = null;
    this.syncVariantState();
    setTimeout(() => {
      try {
        input.setSelectionRange(0, 0);
      } catch { }
    }, 0);
  }

  onVariantQuantityChange(row: { available: number | null }, value: string | number | null): void {
    row.available = this.normalizeQuantity(value);
    this.syncVariantState();
  }

  onVariantImageSelect(row: { image: string | null }, event: Event): void {
    if (this.isViewOnly) return;
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      row.image = typeof reader.result === 'string' ? reader.result : null;
      this.syncVariantState();
    };
    reader.readAsDataURL(file);
    if (input) {
      input.value = '';
    }
  }

  clearVariantImage(row: { image: string | null }): void {
    if (this.isViewOnly) return;
    row.image = null;
    this.syncVariantState();
  }

  openShippingPanel(row: any): void {
    if (this.isViewOnly) return;
    this.activeShippingRow = row;
    this.shippingDirty = false;
    this.applyShippingToAllVariants = false;
    this.activeShippingDraft = {
      shippingWeight: row.shippingWeight ?? this._productService.shippingWeight ?? 0,
      shippingHeight: row.shippingHeight ?? this._productService.shippingHeight ?? 0,
      shippingWidth: row.shippingWidth ?? this._productService.shippingWidth ?? 0,
      shippingLength: row.shippingLength ?? this._productService.shippingLength ?? 0,
      shippingDimensionUOM: row.shippingDimensionUOM ?? this._productService.selectedShippingDimension?.id ?? this.defaultDimensionUomId,
    };
    this.isShippingDialogVisible = true;
  }

  /** Resolves to the "in" entry by name rather than assuming it sits at a fixed array index. */
  private get defaultDimensionUomId(): number | string | null {
    const list = this._abd.DimensionUOM;
    if (!Array.isArray(list) || !list.length) return null;
    const inch = list.find((d: any) => String(d?.title ?? '').trim().toLowerCase() === 'in');
    return inch?.id ?? list[0]?.id ?? null;
  }

  /** Explicit "Save" action — the only path that commits the draft. Typing a value and then
   * closing the dialog via the X button / backdrop / Esc must NOT save it (that was the old
   * behavior: any edit + any close committed, so there was no way to try a value and back out). */
  saveShippingDialog(): void {
    if (this.activeShippingRow && this.activeShippingDraft) {
      const draft = this.activeShippingDraft;
      const targetRows: any[] = this.applyShippingToAllVariants
        ? this.variantRows
        : [this.activeShippingRow];
      targetRows.forEach((row: any) => {
        row.shippingWeight = draft.shippingWeight ?? 0;
        row.shippingHeight = draft.shippingHeight ?? 0;
        row.shippingWidth = draft.shippingWidth ?? 0;
        row.shippingLength = draft.shippingLength ?? 0;
        row.shippingDimensionUOM = draft.shippingDimensionUOM;
      });
      this.syncVariantState();
    }
    this.isShippingDialogVisible = false;
  }

  /** Explicit "Cancel" action — discards the draft without writing anything. */
  cancelShippingDialog(): void {
    this.isShippingDialogVisible = false;
  }

  /** Zeroes out the weight/dimension fields in the draft — the only way to clear a variant's
   * shipping in one action instead of blanking all four number fields by hand. Still requires
   * Save to actually commit, same as any other edit. */
  resetShippingDraft(): void {
    if (this.isViewOnly || !this.activeShippingDraft) return;
    this.activeShippingDraft.shippingWeight = 0;
    this.activeShippingDraft.shippingHeight = 0;
    this.activeShippingDraft.shippingWidth = 0;
    this.activeShippingDraft.shippingLength = 0;
    this.shippingDirty = true;
  }

  /** Fires on every close path (Save, Cancel, X icon, backdrop click, Esc). Writing already
   * happened in saveShippingDialog() if the user actually saved — this only resets state so
   * reopening the dialog starts clean, and guarantees the X/backdrop/Esc paths never write. */
  onShippingPanelHide(): void {
    this.shippingDirty = false;
    this.applyShippingToAllVariants = false;
    this.activeShippingRow = null;
    this.activeShippingDraft = null;
  }

  onApplyShippingToAllChange(checked: boolean): void {
    if (!checked) {
      this.applyShippingToAllVariants = false;
      this.shippingDirty = true;
      return;
    }
    // Checking this box silently overwrites every variant's shipping data on close —
    // confirm first since there's no undo once onShippingPanelHide() writes it out.
    const count = this.variantRows.length;
    this._confirmationService.confirm({
      header: 'Apply shipping to all variants?',
      message: `This replaces the shipping weight and dimensions on all ${count} variant${count === 1 ? '' : 's'} with this row's values. Existing per-variant shipping data will be overwritten.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Apply to all',
      rejectLabel: 'Cancel',
      accept: () => {
        this.applyShippingToAllVariants = true;
        this.shippingDirty = true;
      },
      reject: () => {
        this.applyShippingToAllVariants = false;
      },
    });
  }

  /** Changing the shared Weight Unit doesn't convert existing variant weight values (a "5" that
   * meant 5 lb becomes 5 kg) — on an existing product being edited, confirm first since that's a
   * meaningful, easy-to-miss change. New products (nothing saved yet) apply it straight away. */
  onVariantWeightUomChange(value: any): void {
    if (this.isViewOnly) return;

    if (!this._productService.isEdit) {
      this._productService.selectedShippingWeight = value;
      return;
    }

    const previous = this._productService.selectedShippingWeight;
    this._confirmationService.confirm({
      header: 'Change weight unit?',
      message: `This changes every variant's weight unit to "${value?.title}". Existing weight values will be interpreted in the new unit, not converted.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Change unit',
      rejectLabel: 'Cancel',
      accept: () => {
        this._productService.selectedShippingWeight = value;
      },
      reject: () => {
        this._productService.selectedShippingWeight = previous;
      },
    });
  }

  getDraftDimensionUomOption(): any {
    return this._abd.DimensionUOM?.find((d: any) => d.id === this.activeShippingDraft?.shippingDimensionUOM) ?? null;
  }

  onDraftDimensionUomChange(value: any): void {
    if (!this.activeShippingDraft) return;
    this.activeShippingDraft.shippingDimensionUOM = value?.id ?? null;
    this.shippingDirty = true;
  }

  onDraftShippingFieldChange(
    field: 'shippingWeight' | 'shippingHeight' | 'shippingWidth' | 'shippingLength',
    value: string | number | null
  ): void {
    if (!this.activeShippingDraft) return;
    // Store the raw value (including null while the field is mid-edit/empty) rather than
    // coercing to 0 here — writing 0 back into the bound p-inputNumber on every keystroke
    // resets its internal caret/formatting, which made typing a digit after backspacing
    // insert before the placeholder "0" (e.g. typing "8" produced "80"). onDraftShippingFieldBlur
    // clamps an empty field back to 0 once the user leaves it.
    const num = value === '' || value === null || value === undefined ? null : Number(value);
    this.activeShippingDraft[field] = num === null || isNaN(num) ? null : num;
    this.shippingDirty = true;
  }

  onDraftShippingFieldFocus(
    field: 'shippingWeight' | 'shippingHeight' | 'shippingWidth' | 'shippingLength',
    event: FocusEvent
  ): void {
    if (!this.activeShippingDraft) return;
    const input = event?.target as HTMLInputElement | null;
    if (!input) return;
    const rawValue = (input.value || '').trim();
    // Same fix as onVariantPriceFocus: a field displaying "0.00" places the caret at the
    // start on focus, so typing a digit inserts before it (e.g. typing "5" into "0.00"
    // produces "50.00"). Clearing the value on focus lets the typed digit land cleanly.
    if (!this.isZeroDisplayValue(rawValue)) return;
    this.activeShippingDraft[field] = null;
    setTimeout(() => {
      try {
        input.setSelectionRange(0, 0);
      } catch { }
    }, 0);
  }

  onDraftShippingFieldBlur(
    field: 'shippingWeight' | 'shippingHeight' | 'shippingWidth' | 'shippingLength'
  ): void {
    if (!this.activeShippingDraft) return;
    if (this.activeShippingDraft[field] === null || this.activeShippingDraft[field] === undefined) {
      this.activeShippingDraft[field] = 0;
    }
  }

  // Weight Unit is a single product-wide setting (variants toolbar dropdown, bound to
  // _productService.selectedShippingWeight) — no longer per-variant, so this always resolves to
  // the product-level unit regardless of any stale row.shippingWeightUOM from before this change.
  getShippingWeightUomOption(): any {
    return this._productService.selectedShippingWeight ?? null;
  }

  getShippingDimensionUomOption(row: any): any {
    return this._abd.DimensionUOM?.find((d: any) => d.id === row?.shippingDimensionUOM) ?? null;
  }

  private getEffectiveShippingDimensionUOM(row: any): number | string | null {
    return row?.shippingDimensionUOM ?? this._productService.selectedShippingDimension?.id ?? null;
  }

  private getMajorityUnit(getUnit: (row: any) => number | string | null): number | string | null {
    const counts = new Map<string, { value: number | string; count: number }>();
    for (const row of this.variantRows) {
      const unit = getUnit(row);
      if (unit === null || unit === undefined) continue;
      const key = String(unit);
      const entry = counts.get(key);
      if (entry) {
        entry.count++;
      } else {
        counts.set(key, { value: unit, count: 1 });
      }
    }
    let best: { value: number | string; count: number } | null = null;
    for (const entry of counts.values()) {
      if (!best || entry.count > best.count) best = entry;
    }
    return best?.value ?? null;
  }

  get majorityShippingDimensionUOM(): number | string | null {
    return this.getMajorityUnit((row) => this.getEffectiveShippingDimensionUOM(row));
  }

  /** True when this row's dimension unit differs from whichever unit most other variants use —
   * drives a warning badge so a stray unit (e.g. one row in cm among in rows) isn't missed at a
   * glance. Weight Unit can't mismatch anymore — it's a single product-wide setting. */
  isShippingUnitMismatched(row: any): boolean {
    if (this.variantRows.length < 2) return false;
    const dimensionUnit = this.getEffectiveShippingDimensionUOM(row);
    const majorityDimensionUnit = this.majorityShippingDimensionUOM;
    return dimensionUnit !== null && majorityDimensionUnit !== null && String(dimensionUnit) !== String(majorityDimensionUnit);
  }

  getShippingSummary(row: any): string {
    // Shows the effective weight/dimensions (this variant's own values if set, otherwise
    // the product-level default) purely for display — reading here never writes back to
    // the row, so glancing at a variant can't silently override its shipping data.
    const weight = Number(row?.shippingWeight ?? this._productService.shippingWeight ?? 0);
    const weightUnit = this.getShippingWeightUomOption()?.title ?? '';
    const height = Number(row?.shippingHeight ?? this._productService.shippingHeight ?? 0);
    const width = Number(row?.shippingWidth ?? this._productService.shippingWidth ?? 0);
    const depth = Number(row?.shippingLength ?? this._productService.shippingLength ?? 0);
    const dimUnit = this.getShippingDimensionUomOption(row)?.title ?? this._productService.selectedShippingDimension?.title ?? '';
    const weightPart = `${weight.toFixed(2)} ${weightUnit}`.trim();
    const dimsPart = `${height.toFixed(2)}×${width.toFixed(2)}×${depth.toFixed(2)} ${dimUnit}`.trim();
    return `${weightPart} · ${dimsPart}`;
  }

  onBulkQuantityChange(value: string | number | null): void {
    this.bulkEditValue = this.normalizeQuantity(value);
  }

  onQuantityKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const allowed = new Set(['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End']);
    if (allowed.has(event.key)) return;
    if (/^\d$/.test(event.key)) return;
    event.preventDefault();
  }

  private normalizeAttributes(raw: any[]): VariantOption[] {
    if (!Array.isArray(raw)) return [];

    return raw.map((item) => {
      const rawId = item?.id ?? item?.Id ?? item?.optionId ?? item?.OptionId ?? null;
      const name = this.normalizeName(
        item?.name ?? item?.Name ?? item?.optionName ?? item?.title ?? ''
      );
      const rawValues =
        item?.values ?? item?.Values ?? item?.optionValues ?? item?.value ?? [];
      let valueIds: Record<string, number | string> | undefined =
        item?.valueIds ?? item?.ValueIds ?? item?.valueIdMap ?? item?.ValueIdMap;
      let colorValueMap: Record<string, string> | undefined =
        item?.colorValueMap ?? item?.ColorValueMap ?? item?.valueMap ?? item?.ValueMap;
      let values: string[] = [];
      if (Array.isArray(rawValues)) {
        if (rawValues.length && typeof rawValues[0] === 'object') {
          values = rawValues
            .map((v: any) => String(v?.displayValue ?? v?.DisplayValue ?? v?.value ?? v?.Value ?? v?.name ?? v?.Name ?? ''))
            .filter((v) => v.length > 0);
          if (!colorValueMap) {
            colorValueMap = {};
            rawValues.forEach((v: any) => {
              const display = String(v?.displayValue ?? v?.DisplayValue ?? v?.name ?? v?.Name ?? '').trim();
              const storage = this.normalizeHexValue(v?.value ?? v?.Value);
              if (display && storage) {
                colorValueMap![display] = storage;
              }
            });
            if (!Object.keys(colorValueMap).length) {
              colorValueMap = undefined;
            }
          }
          if (!valueIds) {
            valueIds = {};
            rawValues.forEach((v: any) => {
              const display = String(v?.displayValue ?? v?.DisplayValue ?? v?.value ?? v?.Value ?? v?.name ?? v?.Name ?? '').trim();
              const id = v?.id ?? v?.Id ?? v?.valueId ?? v?.ValueId ?? v?.optionValueId ?? v?.OptionValueId;
              if (display && id !== undefined && id !== null) {
                valueIds![display] = id;
              }
            });
          }
        } else {
          values = rawValues.map((v: any) => String(v));
        }
      } else if (typeof rawValues === 'string') {
        values = rawValues.split(',').map((v) => v.trim());
      }
      const normalized: VariantOption = {
        id: rawId,
        name,
        values: this.normalizeValues(values),
        optionType: this.resolveOptionType(name),
        isCustomName: !this.isBaseOptionName(name),
        valueIds,
        colorValueMap
      };
      if (this.isColorOption(normalized)) {
        normalized.values = normalized.values.map((value) => this.toTitleCase(value));
      } else if (this.isTargetGenderOption(normalized)) {
        normalized.values = this.sortGenderOptions(normalized.values);
      }
      this.updateOptionDefaults(normalized);
      return normalized;
    });
  }

  private normalizeName(name: string): string {
    if (!name) return '';
    return String(name).trim();
  }

  private isBaseOptionName(name: string): boolean {
    const normalized = this.normalizeName(name).toLowerCase();
    if (!normalized) return false;
    return this.baseOptionSuggestions.some(
      (item) => item.toLowerCase() === normalized
    );
  }

  private addOptionSuggestion(name: string): void {
    this.optionSuggestions = this.getActiveOptionSuggestions();
  }

  private updateAddOptionSuggestions(rawQuery: string): void {
    const query = (rawQuery || '').trim().toLowerCase();
    const usedNames = new Set(
      this.options
        .map((opt) => this.normalizeName(opt.name).toLowerCase())
        .filter((name) => !!name)
    );
    const base = this.getActiveOptionSuggestions().filter(
      (item) => !usedNames.has(item.toLowerCase())
    );
    if (!query) {
      this.filteredAddOptionSuggestions = [...base];
      this.highlightedAddOptionIndex = this.filteredAddOptionSuggestions.length ? 0 : -1;
      return;
    }
    this.filteredAddOptionSuggestions = base.filter((item) =>
      item.toLowerCase().includes(query)
    );
    this.highlightedAddOptionIndex = this.filteredAddOptionSuggestions.length ? 0 : -1;
  }

  private getActiveOptionSuggestions(excludeIndex?: number): string[] {
    const suggestions: string[] = [];
    const seen = new Set<string>();
    const add = (name: string) => {
      const normalized = this.normalizeName(name);
      if (!normalized) return;
      const key = normalized.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      suggestions.push(normalized);
    };
    this.baseOptionSuggestions.forEach((name) => add(name));
    this.options.forEach((option, idx) => {
      if (excludeIndex !== undefined && idx === excludeIndex) return;
      const name = option?.name || '';
      const normalized = this.normalizeName(name);
      if (!normalized) return;
      if (option?.isCustomName && !(option?.values?.length > 0)) {
        return;
      }
      add(normalized);
    });
    return suggestions;
  }

  private getOptionUniquenessKey(name: string): string {
    const normalized = this.normalizeName(name).toLowerCase();
    if (!normalized) return '';
    const type = this.resolveOptionType(normalized);
    if (type && type !== 'other') {
      return `type:${type}`;
    }
    return `name:${normalized}`;
  }

  private findDuplicateOptionIndex(name: string, ignoreIndex?: number): number {
    const targetKey = this.getOptionUniquenessKey(name);
    if (!targetKey) return -1;
    return this.options.findIndex((opt, idx) => {
      if (idx === (ignoreIndex ?? -1)) return false;
      return this.getOptionUniquenessKey(opt?.name || '') === targetKey;
    });
  }

  private isOptionNameAvailable(name: string, ignoreIndex?: number): boolean {
    return this.findDuplicateOptionIndex(name, ignoreIndex) < 0;
  }

  canCreateCustomOption(index: number): boolean {
    const option = this.options[index];
    if (!option) return false;
    const normalized = this.normalizeName(option.name);
    if (!normalized) return false;
    if (normalized.length > this.maxCustomOptionLength) return false;
    return this.isOptionNameAvailable(normalized, index);
  }

  private resolveOptionType(name: string): string {
    const normalized = this.normalizeName(name).toLowerCase();
    if (!normalized) return 'other';
    if (normalized === 'color' || normalized === 'colour' || normalized.includes('color') || normalized.includes('colour')) return 'color';
    if (normalized === 'size' || normalized.includes('size')) return 'size';
    if (normalized === 'material' || normalized.includes('material')) return 'material';
    if (normalized === 'style' || normalized.includes('style')) return 'style';
    if (normalized === 'target gender' || normalized === 'gender' || normalized.includes('gender')) return 'gender';
    if (normalized === 'length' || normalized.includes('length')) return 'length';
    return 'other';
  }

  private updateOptionDefaults(option: VariantOption): void {
    if (!option) return;
    option.cachedColorOptions = [...this.baseColorOptions];
    option.cachedSizeOptions = [...this.baseSizeOptionItems];
    option.cachedMaterialOptions = [...this.baseMaterialOptionItems];
    option.cachedStyleOptions = [...this.baseStyleOptionItems];
    option.cachedTargetGenderOptions = [...this.baseTargetGenderOptionItems];
    option.cachedLengthOptions = [...this.baseLengthOptionItems];
    this.applyPresetOptionId(option);
    this.applyPresetValueIds(option);
    this.mergeCustomValuesIntoOptions(option);
  }

  private mergeCustomValuesIntoOptions(option: VariantOption): void {
    const values = Array.isArray(option?.values) ? option.values : [];
    if (!values.length) return;

    if (this.isColorOption(option)) {
      const existing = new Set(
        (option.cachedColorOptions || []).map((item) => item.name.toLowerCase())
      );
      values.forEach((value) => {
        const name = String(value || '').trim();
        if (!name) return;
        const key = name.toLowerCase();
        if (existing.has(key)) return;
        existing.add(key);
        const hex = this.getValueSwatch(option, name) ?? '#ffffff';
        option.cachedColorOptions = [
          ...(option.cachedColorOptions || []),
          { name, hex }
        ];
      });
      return;
    }

    const mergeList = (
      list: Array<{ label: string; value: string }> | undefined
    ): Array<{ label: string; value: string }> => {
      const existing = new Set((list || []).map((item) => item.value.toLowerCase()));
      let next = list ? [...list] : [];
      values.forEach((value) => {
        const label = String(value || '').trim();
        if (!label) return;
        const key = label.toLowerCase();
        if (existing.has(key)) return;
        existing.add(key);
        next.push({ label, value: label });
      });
      return next;
    };

    if (this.isSizeOption(option)) {
      option.cachedSizeOptions = mergeList(option.cachedSizeOptions);
      return;
    }
    if (this.isMaterialOption(option)) {
      option.cachedMaterialOptions = mergeList(option.cachedMaterialOptions);
      return;
    }
    if (this.isStyleOption(option)) {
      option.cachedStyleOptions = mergeList(option.cachedStyleOptions);
      return;
    }
    if (this.isTargetGenderOption(option)) {
      option.cachedTargetGenderOptions = this.sortGenderOptionItems(mergeList(option.cachedTargetGenderOptions));
      return;
    }
    if (this.isLengthOption(option)) {
      option.cachedLengthOptions = mergeList(option.cachedLengthOptions);
      return;
    }
  }

  private buildColorOptions(option: VariantOption): ColorOption[] {
    return [...this.baseColorOptions];
  }

  private buildDefaultOptions(baseList: string[], values: string[]): Array<{ label: string; value: string }> {
    return baseList
      .map((value) => ({
        label: value,
        value
      }));
  }

  private loadVariantOptionPresets(): void {
    this.isPresetLoading = true;
    this.presetLoadError = '';
    this._productService
      .getVariantOptionPresets()
      .pipe(
        timeout(10000),
        catchError(() => {
          this.presetLoadError = 'Unable to load recommended options.';
          return of(null);
        })
      )
      .subscribe({
      next: (res: any) => {
        this.isPresetLoading = false;
        if (!res || res.success === false) return;
        const payload = res?.data ?? res;
        this.applyVariantOptionPresets(payload);
      },
      error: () => {
        this.isPresetLoading = false;
        this.presetLoadError = 'Unable to load recommended options.';
      }
      });
  }

  private shouldIgnoreDuplicateAddOptionAction(): boolean {
    const now = Date.now();
    if (now - this.lastAddOptionActionAt < 350) {
      return true;
    }
    this.lastAddOptionActionAt = now;
    return false;
  }

  private detectAndroidOs(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }
    const userAgent = String(navigator.userAgent || '').toLowerCase();
    return userAgent.includes('android');
  }

  private applyVariantOptionPresets(payload: any): void {
    const normalizedPayload = this.normalizePresetPayload(payload);
    this.updateBaseOptionSuggestionsFromPresets(normalizedPayload);
    this.storePresetOptionIds(normalizedPayload);

    const colorList = this.getPresetList(normalizedPayload, ['color', 'colors', 'colour', 'colours', 'colorOptions', 'colourOptions']);
    this.storePresetValueIds('color', colorList);
    const colors = this.normalizeColorPresets(colorList);
    if (colors.length) {
      this.baseColorOptions = colors;
    }

    const sizeList = this.getPresetList(normalizedPayload, ['size', 'sizes', 'sizeOptions']);
    this.storePresetValueIds('size', sizeList);
    const sizes = this.sortSizeOptions(this.normalizePresetValues(sizeList));
    if (sizes.length) {
      this.baseSizeOptions = sizes;
    }

    const materialList = this.getPresetList(normalizedPayload, ['material', 'materials', 'materialOptions']);
    this.storePresetValueIds('material', materialList);
    const materials = this.normalizePresetValues(materialList);
    if (materials.length) {
      this.baseMaterialOptions = materials;
    }

    const styleList = this.getPresetList(normalizedPayload, ['style', 'styles', 'styleOptions']);
    this.storePresetValueIds('style', styleList);
    const styles = this.normalizePresetValues(styleList);
    if (styles.length) {
      this.baseStyleOptions = styles;
    }

    const genderList = this.getPresetList(normalizedPayload, ['targetGender', 'targetGenders', 'gender', 'genders', 'genderOptions']);
    this.storePresetValueIds('gender', genderList);
    const genders = this.sortGenderOptions(this.normalizePresetValues(genderList));
    if (genders.length) {
      this.baseTargetGenderOptions = genders;
    }

    const lengthList = this.getPresetList(normalizedPayload, ['length', 'lengths', 'lengthOptions']);
    this.storePresetValueIds('length', lengthList);
    const lengths = this.normalizePresetValues(lengthList);
    if (lengths.length) {
      this.baseLengthOptions = lengths;
    }

    this.refreshPresetOptionItems();
    this.options.forEach((option) => this.updateOptionDefaults(option));
  }

  private refreshPresetOptionItems(): void {
    this.baseSizeOptionItems = this.baseSizeOptions.map((value) => ({
      label: value,
      value
    }));
    this.baseMaterialOptionItems = this.baseMaterialOptions.map((value) => ({
      label: value,
      value
    }));
    this.baseStyleOptionItems = this.baseStyleOptions.map((value) => ({
      label: value,
      value
    }));
    this.baseTargetGenderOptionItems = this.sortGenderOptions(this.baseTargetGenderOptions).map((value) => ({
      label: value,
      value
    }));
    this.baseLengthOptionItems = this.baseLengthOptions.map((value) => ({
      label: value,
      value
    }));
  }

  private getPresetList(payload: any, keys: string[]): any[] {
    if (!payload) return [];
    const target = new Set(keys.map((key) => this.normalizePresetKey(key)));
    if (Array.isArray(payload)) {
      const match = payload.find((item) => {
        const name = this.normalizePresetKey(
          item?.name ?? item?.type ?? item?.key ?? item?.option ?? item?.optionType ?? item?.option_type
        );
        return !!name && target.has(name);
      });
      if (!match) return [];
      const list = match?.values ?? match?.options ?? match?.items ?? match?.list ?? match?.presets ?? match?.data;
      return Array.isArray(list) ? list : [];
    }

    if (payload?.presets) {
      const nested = this.getPresetList(payload.presets, keys);
      if (nested.length) return nested;
    }
    if (payload?.options) {
      const nested = this.getPresetList(payload.options, keys);
      if (nested.length) return nested;
    }
    if (payload?.data) {
      const nested = this.getPresetList(payload.data, keys);
      if (nested.length) return nested;
    }

    for (const [key, value] of Object.entries(payload)) {
      if (target.has(this.normalizePresetKey(key)) && Array.isArray(value)) {
        return value as any[];
      }
    }
    return [];
  }

  private normalizePresetPayload(payload: any): any {
    if (!payload) return payload;
    const entryList = this.extractPresetEntries(payload);
    if (!entryList.length) return payload;

    const grouped: Record<string, any[]> = {};
    for (const item of entryList) {
      const typeKey = this.normalizePresetTypeKey(
        item?.optionType ?? item?.option_type ?? item?.option ?? item?.type ?? item?.name ?? item?.key
      );
      if (!typeKey) continue;
      if (!grouped[typeKey]) grouped[typeKey] = [];
      grouped[typeKey].push(item);
    }

    return Object.keys(grouped).length ? grouped : payload;
  }

  private extractPresetEntries(payload: any): any[] {
    if (!payload) return [];
    if (!Array.isArray(payload)) {
      if (Array.isArray(payload?.data)) return this.extractPresetEntries(payload.data);
      if (Array.isArray(payload?.options)) return this.extractPresetEntries(payload.options);
      if (Array.isArray(payload?.presets)) return this.extractPresetEntries(payload.presets);
      if (Array.isArray(payload?.items)) return this.extractPresetEntries(payload.items);
      if (Array.isArray(payload?.list)) return this.extractPresetEntries(payload.list);
      return [];
    }
    const hasOptionType = payload.some((item) =>
      this.normalizePresetTypeKey(
        item?.optionType ?? item?.option_type ?? item?.option ?? item?.type ?? item?.name ?? item?.key
      )
    );
    const hasValueLike = payload.some((item) =>
      item && (item?.displayValue ?? item?.display ?? item?.value ?? item?.label ?? item?.name ?? item?.text)
    );
    const hasNestedValues = payload.some((item) =>
      Array.isArray(item?.values) || Array.isArray(item?.options) || Array.isArray(item?.items) || Array.isArray(item?.list)
    );

    return hasOptionType && hasValueLike && !hasNestedValues ? payload : [];
  }

  private updateBaseOptionSuggestionsFromPresets(payload: any): void {
    const types = this.collectPresetOptionTypes(payload);
    if (!types.length) return;
    const suggestions = this.buildSuggestedOptionNames(types);
    if (!suggestions.length) return;
    this.baseOptionSuggestions = suggestions;
    this.optionSuggestions = this.getActiveOptionSuggestions();
    this.updateAddOptionSuggestions(this.addOptionSearch);
  }

  private collectPresetOptionTypes(payload: any): string[] {
    const seen = new Set<string>();
    const types: string[] = [];
    const addType = (value: any) => {
      const key = this.normalizePresetTypeKey(value);
      if (!key || seen.has(key)) return;
      seen.add(key);
      types.push(key);
    };

    if (Array.isArray(payload)) {
      payload.forEach((item) => {
        addType(item?.optionType ?? item?.option_type ?? item?.option ?? item?.type ?? item?.name ?? item?.key);
      });
      return types;
    }

    if (payload && typeof payload === 'object') {
      if (Array.isArray(payload.data)) return this.collectPresetOptionTypes(payload.data);
      if (Array.isArray(payload.options)) return this.collectPresetOptionTypes(payload.options);
      if (Array.isArray(payload.presets)) return this.collectPresetOptionTypes(payload.presets);
      if (Array.isArray(payload.items)) return this.collectPresetOptionTypes(payload.items);
      if (Array.isArray(payload.list)) return this.collectPresetOptionTypes(payload.list);

      Object.keys(payload).forEach((key) => {
        if (Array.isArray((payload as Record<string, any>)[key])) {
          addType(key);
        }
      });
    }
    return types;
  }

  private buildSuggestedOptionNames(types: string[]): string[] {
    const result: string[] = [];
    const added = new Set<string>();
    const addLabel = (key: string) => {
      const label = this.standardOptionTypeLabels[key] ?? this.toTitleCase(key);
      if (!label) return;
      const normalized = label.toLowerCase();
      if (added.has(normalized)) return;
      added.add(normalized);
      result.push(label);
    };

    this.standardOptionTypeOrder.forEach((key) => addLabel(key));
    types.forEach((key) => {
      if (!this.standardOptionTypeOrder.includes(key)) addLabel(key);
    });

    return result.length ? result : [...this.fallbackOptionSuggestions];
  }

  private normalizePresetTypeKey(value: any): string {
    const normalized = this.normalizePresetKey(value);
    if (!normalized) return '';
    if (normalized.includes('color') || normalized.includes('colour')) return 'color';
    if (normalized.includes('size')) return 'size';
    if (normalized.includes('material')) return 'material';
    if (normalized.includes('style')) return 'style';
    if (normalized.includes('gender')) return 'gender';
    if (normalized.includes('length')) return 'length';
    return normalized;
  }

  private normalizePresetKey(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
  }

  private normalizePresetValues(list: any[]): string[] {
    if (!Array.isArray(list)) return [];
    const cleaned = list
      .map((item) => this.extractPresetLabel(item))
      .map((value) => String(value).trim())
      .filter((value) => value.length > 0);
    return Array.from(new Set(cleaned));
  }

  private sortSizeOptions(values: string[]): string[] {
    return [...values].sort((a, b) => {
      const rankA = this.getSizeSortRank(a);
      const rankB = this.getSizeSortRank(b);
      if (rankA !== rankB) return rankA - rankB;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  private sortGenderOptions(values: string[]): string[] {
    return [...values].sort((a, b) => {
      const rankA = this.getGenderSortRank(a);
      const rankB = this.getGenderSortRank(b);
      if (rankA !== rankB) return rankA - rankB;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  private sortGenderOptionItems(items: Array<{ label: string; value: string }>): Array<{ label: string; value: string }> {
    return [...items].sort((a, b) => {
      const rankA = this.getGenderSortRank(a?.value ?? a?.label);
      const rankB = this.getGenderSortRank(b?.value ?? b?.label);
      if (rankA !== rankB) return rankA - rankB;
      return String(a?.label ?? '').localeCompare(String(b?.label ?? ''), undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  private getSizeSortRank(value: string): number {
    const normalized = String(value ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
    if (!normalized) return Number.MAX_SAFE_INTEGER;
    if (this.standardSizeOrder[normalized] !== undefined) {
      return this.standardSizeOrder[normalized];
    }
    const numericMatch = normalized.match(/^(\d+)(xl|x)$/);
    if (numericMatch) {
      return 70 + Number(numericMatch[1]) * 10;
    }
    return 10000;
  }

  private getGenderSortRank(value: any): number {
    const normalized = this.normalizePresetKey(value);
    if (!normalized) return Number.MAX_SAFE_INTEGER;
    return this.standardGenderOrder[normalized] ?? 10000;
  }

  private normalizeColorPresets(list: any[]): ColorOption[] {
    if (!Array.isArray(list)) return [];
    const fallbackMap = new Map(
      this.fallbackColorOptions.map((item) => [item.name.toLowerCase(), item.hex])
    );
    const seen = new Set<string>();
    const result: ColorOption[] = [];
    for (const item of list) {
      let name = '';
      let hex: string | undefined;
      let id: number | string | null = null;
      if (typeof item === 'string' || typeof item === 'number') {
        name = String(item);
      } else if (item && typeof item === 'object') {
        name = this.extractPresetLabel(item) || '';
        hex = item?.hex ?? item?.hexValue ?? item?.colorHex ?? item?.colourHex;
        id = this.extractPresetId(item);
      }
      name = String(name || '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const normalizedHex = this.normalizeHexValue(hex) ?? fallbackMap.get(key) ?? '#ffffff';
      result.push({ name, hex: normalizedHex, id: id ?? undefined });
    }
    return result;
  }

  private extractPresetLabel(item: any): string {
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (!item || typeof item !== 'object') return '';
    return item?.displayValue ?? item?.display ?? item?.value ?? item?.label ?? item?.name ?? item?.text ?? '';
  }

  private extractPresetId(item: any): number | string | null {
    if (!item || typeof item !== 'object') return null;
    const raw =
      item?.id ??
      item?.Id ??
      item?.optionId ??
      item?.option_id ??
      item?.optionID ??
      item?.optionValueId ??
      item?.option_value_id ??
      item?.optionValueID ??
      item?.optionvalueid ??
      item?.valueId ??
      item?.valueID ??
      item?.ValueId ??
      item?.ValueID ??
      null;
    if (raw === undefined || raw === null || raw === '') return null;
    return raw;
  }

  private normalizeValueKey(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private getKnownColorHex(value: any): string | null {
    const key = this.normalizeValueKey(value);
    if (!key) return null;
    const known = this.baseColorOptions.find(
      (item) => this.normalizeValueKey(item?.name) === key
    );
    return this.normalizeHexValue(known?.hex);
  }

  private storePresetValueIds(type: string, list: any[]): void {
    if (!type || !Array.isArray(list)) return;
    const map: Record<string, number | string> = {};
    list.forEach((item) => {
      const label = this.extractPresetLabel(item);
      const id = this.extractPresetId(item);
      const key = this.normalizeValueKey(label);
      if (!key || id === null || id === undefined) return;
      map[key] = id;
    });
    if (Object.keys(map).length) {
      this.presetValueIdMap[type] = map;
    }
  }

  private storePresetOptionIds(payload: any): void {
    const map: Record<string, number | string> = {};
    const store = (typeRaw: any, idRaw: any) => {
      const type = this.normalizePresetTypeKey(typeRaw);
      if (!type) return;
      if (idRaw === undefined || idRaw === null || idRaw === '') return;
      map[type] = idRaw;
    };

    if (Array.isArray(payload)) {
      payload.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        store(item?.optionType ?? item?.option_type ?? item?.option ?? item?.type ?? item?.name ?? item?.key, item?.optionId ?? item?.option_id ?? item?.optionID ?? item?.OptionId ?? item?.OptionID ?? item?.Id ?? item?.id);
      });
    } else if (payload && typeof payload === 'object') {
      Object.entries(payload).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length) {
          const first = value.find((entry) => entry && typeof entry === 'object');
          if (!first) return;
          store(key, first?.optionId ?? first?.option_id ?? first?.optionID ?? first?.OptionId ?? first?.OptionID ?? first?.Id ?? first?.id);
        }
      });
    }

    if (Object.keys(map).length) {
      this.presetOptionIdMap = { ...this.presetOptionIdMap, ...map };
    }
  }

  private applyPresetOptionId(option: VariantOption): void {
    if (!option) return;
    if (option.id !== undefined && option.id !== null && option.id !== '') return;
    const typeKey = this.resolveOptionType(option.name);
    if (!typeKey || !this.presetOptionIdMap[typeKey]) return;
    option.id = this.presetOptionIdMap[typeKey];
  }

  private applyPresetValueIds(option: VariantOption): void {
    if (!option || !Array.isArray(option.values)) return;
    const typeKey = this.resolveOptionType(option.name);
    if (!typeKey || !this.presetValueIdMap[typeKey]) return;
    const map = this.presetValueIdMap[typeKey];
    const nextValueIds: Record<string, number | string> = { ...(option.valueIds || {}) };
    let changed = false;
    option.values.forEach((value) => {
      if (nextValueIds[value] !== undefined) return;
      const key = this.normalizeValueKey(value);
      if (!key) return;
      const presetId = map[key];
      if (presetId !== undefined) {
        nextValueIds[value] = presetId;
        changed = true;
      }
    });
    if (changed) {
      option.valueIds = nextValueIds;
    }
  }

  private normalizeHexValue(value: any): string | null {
    if (!value) return null;
    const hex = String(value).trim();
    return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex) ? hex : null;
  }

  private normalizeValues(values: string[] = []): string[] {
    const cleaned = values
      .map((v) => String(v).trim())
      .filter((v) => v.length > 0);
    return Array.from(new Set(cleaned));
  }

  private getDuplicateValue(values: string[] = []): string | null {
    const seen = new Set<string>();
    for (const value of values) {
      const normalized = this.normalizeValueKey(value);
      if (!normalized) continue;
      if (seen.has(normalized)) {
        return String(value ?? '').trim();
      }
      seen.add(normalized);
    }
    return null;
  }

  private setOptionValueError(index: number, message: string): void {
    this.optionValueErrors.set(index, message);
  }

  private clearOptionValueError(index: number): void {
    this.optionValueErrors.delete(index);
  }

  private toTitleCase(value: string): string {
    return value
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private rebuildVariantRows(): void {
    const previousLocationStocks = this._productService.variantLocationStocks || {};
    const previousLocationQohStocks = this._productService.variantLocationQohStocks || {};
    const activeOptions = this.options
      .map((option) => ({
        name: this.normalizeName(option.name),
        values: option.values || []
      }))
      .filter((option) => option.name && option.values.length);

    if (!activeOptions.length) {
      this.variantRows = [];
      this.filteredVariantRows = [];
      this.selectedVariantIds.clear();
      this.deletedVariantIds.clear();
      this.groupByOption = null;
      this.groupByOptionsList = [];
      this.groupedVariantsList = [];
      this.activeOptionNames = [];
      this.collapsedGroups.clear();
      this._productService.variantLocationStocks = {};
      this._productService.variantLocationQohStocks = {};
      this.syncVariantState();
      return;
    }

    const previousRows = [...this.variantRows];
    const prevMap = new Map(previousRows.map((row) => [row.id, row]));
    const imageMap = new Map<string, string | null>();
    const imageMapByOptions = new Map<string, string | null>();
    const buildOptionsSignature = (options: Record<string, string> | undefined) =>
      Object.entries(options || {})
        .map(([key, val]) => [this.normalizeName(key), String(val ?? '').trim().toLowerCase()] as [string, string])
        .filter(([key, val]) => !!key && !!val)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, val]) => `${key}:${val}`)
        .join('|');
    (this._productService.variantImages || []).forEach((entry) => {
      if (entry?.id) {
        imageMap.set(entry.id, entry.image ?? null);
      }
      const optionsSignature = buildOptionsSignature(entry?.options);
      if (optionsSignature) {
        imageMapByOptions.set(optionsSignature, entry?.image ?? null);
      }
    });

    let totalCombos = 1;
    for (const option of activeOptions) {
      totalCombos *= option.values.length;
    }
    if (totalCombos > this.maxVariantCombinations) {
      this.variantRows = [];
      this.filteredVariantRows = [];
      this.selectedVariantIds.clear();
      this.groupByOptionsList = activeOptions.map((opt) => ({
        label: opt.name,
        value: opt.name
      }));
      this.groupByOption = this.groupByOptionsList[0]?.value ?? null;
      this.groupedVariantsList = [];
      this._productService.variantLocationStocks = {};
      this._productService.variantLocationQohStocks = {};
      this.syncVariantState();
      return;
    }

    let combos: Array<Record<string, string>> = [{}];
    for (const option of activeOptions) {
      const next: Array<Record<string, string>> = [];
      for (const row of combos) {
        for (const value of option.values) {
          next.push({ ...row, [option.name]: value });
        }
      }
      combos = next;
    }

    const skuSeed = this.getDefaultVariantSku();
    const barcodeSeed = this.getDefaultVariantBarcode();
    const usedSkuKeys = new Set<string>();
    const usedBarcodeKeys = new Set<string>();
    const baseSku = this.normalizeCodeValue(skuSeed);
    const baseBarcode = this.normalizeCodeValue(barcodeSeed);
    const preferNumericSku = this.isNumericCode(baseSku);
    const preferNumericBarcode = this.isNumericCode(baseBarcode);

    for (const combo of combos) {
      const id = activeOptions.map((opt) => `${opt.name}:${combo[opt.name]}`).join('|');
      const prev = prevMap.get(id);
      const sku = this.normalizeCodeValue(prev?.sku);
      if (sku) usedSkuKeys.add(this.toCodeKey(sku));
      const barcode = this.normalizeCodeValue(prev?.barcode);
      if (barcode) usedBarcodeKeys.add(this.toCodeKey(barcode));
    }

    const nextRows = combos.map((combo) => {
      const id = activeOptions.map((opt) => `${opt.name}:${combo[opt.name]}`).join('|');
      const prev = prevMap.get(id);
      const inherited = prev || this.findBestInheritedVariantRow(combo, previousRows);
      const optionsSignature = buildOptionsSignature(combo);
      const defaultPrice = this.getDefaultVariantPrice();
      const prevPrice = prev?.price ?? inherited?.price;
      const normalizedPrevPrice = this.formatPrice(prevPrice);
        const existingSku = this.normalizeCodeValue(prev?.sku);
        const existingBarcode = this.normalizeCodeValue(prev?.barcode);
      const rowForCode = { options: combo };
      const reuseSku =
        !!existingSku && (!preferNumericSku || (baseSku && this.toCodeKey(existingSku) !== this.toCodeKey(baseSku)));
      const reuseBarcode =
        !!existingBarcode && (!preferNumericBarcode || (baseBarcode && this.toCodeKey(existingBarcode) !== this.toCodeKey(baseBarcode)));
      return {
        id,
        options: combo,
        price: normalizedPrevPrice ?? defaultPrice,
        available: prev?.available ?? this.getDefaultVariantQuantity(),
        image: prev?.image ?? imageMap.get(id) ?? imageMapByOptions.get(optionsSignature) ?? inherited?.image ?? null,
        sku: this.useParentSkuBarcodeForVariants
          ? (baseSku || 'SKU')
          : (reuseSku ? existingSku : this.buildDefaultVariantCode(skuSeed, rowForCode, usedSkuKeys, 'SKU', preferNumericSku)),
        barcode: this.useParentSkuBarcodeForVariants
          ? (baseBarcode || 'BAR')
          : (reuseBarcode ? existingBarcode : this.buildDefaultVariantCode(barcodeSeed, rowForCode, usedBarcodeKeys, 'BAR', preferNumericBarcode)),
        // Left null (rather than snapshotting the product-level default here) so an
        // un-customized variant keeps inheriting the *current* product-level Shipping
        // Information values at save/display time instead of a stale copy from whenever
        // this row happened to be generated.
        shippingWeight: prev?.shippingWeight ?? inherited?.shippingWeight ?? null,
        shippingHeight: prev?.shippingHeight ?? inherited?.shippingHeight ?? null,
        shippingWidth: prev?.shippingWidth ?? inherited?.shippingWidth ?? null,
        shippingLength: prev?.shippingLength ?? inherited?.shippingLength ?? null,
        shippingWeightUOM: prev?.shippingWeightUOM ?? inherited?.shippingWeightUOM ?? null,
        shippingDimensionUOM: prev?.shippingDimensionUOM ?? inherited?.shippingDimensionUOM ?? null,
      };
    });
    const generatedIds = new Set(nextRows.map((row) => row.id));
    this.variantRows = nextRows.filter((row) => !this.deletedVariantIds.has(row.id));
    this._productService.variantLocationStocks = this.buildInheritedVariantLocationStocks(
      this.variantRows,
      previousRows,
      previousLocationStocks
    );
    this._productService.variantLocationQohStocks = this.buildInheritedVariantLocationStocks(
      this.variantRows,
      previousRows,
      previousLocationQohStocks
    );
    this.syncVariantState();
    this.validateVariantCodes();
    if (this.deletedVariantIds.size) {
      for (const id of Array.from(this.deletedVariantIds)) {
        if (!generatedIds.has(id)) {
          this.deletedVariantIds.delete(id);
        }
      }
    }
    if (this.selectedVariantIds.size) {
      const activeIds = new Set(this.variantRows.map((row) => row.id));
      for (const id of Array.from(this.selectedVariantIds)) {
        if (!activeIds.has(id)) {
          this.selectedVariantIds.delete(id);
        }
      }
      this.buildVariantActionMenu();
    }

    const optionNames = activeOptions.map((opt) => opt.name);
    this.activeOptionNames = optionNames;
    this.groupByOptionsList = optionNames.map((name) => ({ label: name, value: name }));
    if (!this.groupByOption || !optionNames.includes(this.groupByOption)) {
      const prevGroup = this.groupByOption;
      this.groupByOption = optionNames[0];
      if (prevGroup !== this.groupByOption) {
        this.collapsedGroups.clear();
      }
    }
    this.applySearchFilter();
  }

  private initializeVariantRowsFromService(): void {
    const activeIds = new Set(this.variantRows.map((row) => row.id));
    for (const id of Array.from(this.selectedVariantIds)) {
      if (!activeIds.has(id)) {
        this.selectedVariantIds.delete(id);
      }
    }

    const optionNames = this.options
      .map((option) => this.normalizeName(option?.name))
      .filter((name) => !!name && Array.isArray(this.options.find((option) => this.normalizeName(option?.name) === name)?.values));

    this.activeOptionNames = optionNames;
    this.groupByOptionsList = optionNames.map((name) => ({ label: name, value: name }));
    if (!this.groupByOption || !optionNames.includes(this.groupByOption)) {
      this.groupByOption = optionNames[0] ?? null;
      this.collapsedGroups.clear();
    }

    this.syncVariantState();
    this.validateVariantCodes();
    this.applySearchFilter();
  }

  private buildInheritedVariantLocationStocks(
    nextRows: Array<{
      id: string;
      options: Record<string, string>;
      price: number | null;
      available: number | null;
      image: string | null;
      sku: string;
      barcode: string;
      skuId?: number | string | null;
    }>,
    previousRows: Array<{
      id: string;
      options: Record<string, string>;
      price: number | null;
      available: number | null;
      image: string | null;
      sku: string;
      barcode: string;
      skuId?: number | string | null;
    }>,
    previousLocationStocks: Record<string, Record<string, number>>
  ): Record<string, Record<string, number>> {
    const locationEntries = Object.entries(previousLocationStocks || {});
    if (!locationEntries.length) return {};

    const previousById = new Map(previousRows.map((row) => [row.id, row]));
    const nextLocationStocks: Record<string, Record<string, number>> = {};

    for (const nextRow of nextRows) {
      const sourceRow = previousById.get(nextRow.id);
      if (!sourceRow) continue;

      for (const [locationKey, stockBucket] of locationEntries) {
        const qty = this.getQuantityFromVariantStockBucket(stockBucket || {}, sourceRow);
        if (qty === null) continue;

        if (!nextLocationStocks[locationKey]) {
          nextLocationStocks[locationKey] = {};
        }

        nextLocationStocks[locationKey][nextRow.id] = qty;

        const skuKey = this.getVariantStockKeyBySkuCode(nextRow?.sku);
        if (skuKey) nextLocationStocks[locationKey][skuKey] = qty;

        const skuIdKey = this.getVariantStockKeyBySkuId(nextRow?.skuId);
        if (skuIdKey) nextLocationStocks[locationKey][skuIdKey] = qty;

        const labelKey = this.getVariantStockKeyByLabel(nextRow?.options);
        if (labelKey) nextLocationStocks[locationKey][labelKey] = qty;
      }
    }

    return nextLocationStocks;
  }

  private getQuantityFromVariantStockBucket(
    stockBucket: Record<string, number>,
    row: {
      id: string;
      options: Record<string, string>;
      price: number | null;
      available: number | null;
      image: string | null;
      sku: string;
      barcode: string;
      skuId?: number | string | null;
    }
  ): number | null {
    const byRowId = row?.id ? stockBucket[row.id] : undefined;
    if (byRowId !== undefined && byRowId !== null) return this.toStockNumber(byRowId);

    const skuKey = this.getVariantStockKeyBySkuCode(row?.sku);
    const bySku = skuKey ? stockBucket[skuKey] : undefined;
    if (bySku !== undefined && bySku !== null) return this.toStockNumber(bySku);

    const skuIdKey = this.getVariantStockKeyBySkuId(row?.skuId);
    const bySkuId = skuIdKey ? stockBucket[skuIdKey] : undefined;
    if (bySkuId !== undefined && bySkuId !== null) return this.toStockNumber(bySkuId);

    const byLabel = stockBucket[this.getVariantStockKeyByLabel(row?.options)];
    if (byLabel !== undefined && byLabel !== null) return this.toStockNumber(byLabel);

    const byLegacyLabel = stockBucket[this.getLegacyVariantStockKeyByLabel(row?.options)];
    if (byLegacyLabel !== undefined && byLegacyLabel !== null) return this.toStockNumber(byLegacyLabel);

    return null;
  }

  private toStockNumber(value: any): number {
    const parsed = typeof value === 'number' ? value : Number(String(value ?? '').trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getVariantStockKeyBySkuCode(skuCode: any): string {
    const raw = String(skuCode ?? '').trim();
    if (!raw) return '';
    if (/^\d+$/.test(raw)) {
      const normalized = raw.replace(/^0+/, '') || '0';
      return `sku:${normalized}`;
    }
    return `sku:${raw.toLowerCase()}`;
  }

  private getVariantStockKeyBySkuId(skuId: any): string {
    if (skuId === undefined || skuId === null) return '';
    const value = String(skuId).trim();
    if (!value) return '';
    return `skuid:${value.toLowerCase()}`;
  }

  private getVariantStockKeyByLabel(options: Record<string, string> | undefined): string {
    if (!options) return '';
    const value = Object.values(options)
      .map((item) => String(item ?? '').trim())
      .filter((item) => !!item)
      .join(' / ')
      .toLowerCase();
    return value ? `variant:${value}` : '';
  }

  private getLegacyVariantStockKeyByLabel(options: Record<string, string> | undefined): string {
    if (!options) return '';
    const value = Object.entries(options)
      .map(([name, val]) => `${String(name ?? '').trim()}:${String(val ?? '').trim()}`)
      .join('/')
      .replace(/\s+/g, '')
      .toLowerCase();
    return value ? `vlabel:${value}` : '';
  }

  private buildGroupedVariants(rows: Array<{ id: string; options: Record<string, string>; price: number | null; available: number | null; image: string | null; sku: string; barcode: string; skuId?: number | string | null }> = this.variantRows): Array<{
    key: string;
    label: string;
    rows: Array<{ id: string; options: Record<string, string>; price: number | null; available: number | null; image: string | null; sku: string; barcode: string; skuId?: number | string | null }>;
  }> {
    if (this.activeOptionNames.length <= 1) {
      return [
        {
          key: 'all',
          label: 'All variants',
          rows
        }
      ];
    }
    if (!this.groupByOption) {
      return [
        {
          key: 'all',
          label: 'All variants',
          rows
        }
      ];
    }

    const groups = new Map<string, any>();
    for (const row of rows) {
      const label = row.options[this.groupByOption] || 'Other';
      if (!groups.has(label)) {
        groups.set(label, { key: label, label, rows: [] });
      }
      groups.get(label).rows.push(row);
    }
    return Array.from(groups.values());
  }

  private applySearchFilter(): void {
    const rawQuery = (this.searchQuery || '').trim();
    if (!rawQuery) {
      this.filteredVariantRows = [...this.variantRows];
      this.groupedVariantsList = this.buildGroupedVariants(this.filteredVariantRows);
      return;
    }
    const query = rawQuery.toLowerCase();
    const normalizedQuery = this.normalizeSearchText(rawQuery);
    this.collapsedGroups.clear();
    this.filteredVariantRows = this.variantRows.filter((row) =>
      this.rowMatchesSearch(row, query, normalizedQuery)
    );
    this.groupedVariantsList = this.filteredVariantRows.length
      ? this.buildGroupedVariants(this.filteredVariantRows)
      : [];
  }

  private rowMatchesSearch(
    row: { options: Record<string, string>; sku?: string; barcode?: string; available?: number | null },
    query: string,
    normalizedQuery: string
  ): boolean {
    const candidates: string[] = [];
    const entries = Object.entries(row.options || {});
    for (const [key, value] of entries) {
      candidates.push(String(key ?? ''));
      candidates.push(String(value ?? ''));
    }
    if (row.sku) candidates.push(String(row.sku));
    if (row.barcode) candidates.push(String(row.barcode));
    if (row.available !== null && row.available !== undefined) {
      candidates.push(String(row.available));
    }
    const variantLabel = this.getVariantLabel({ options: row.options });
    if (variantLabel) {
      candidates.push(variantLabel);
    }

    for (const candidate of candidates) {
      if (this.matchesSearchText(candidate, query, normalizedQuery)) {
        return true;
      }
    }
    return false;
  }

  private matchesSearchText(value: string, query: string, normalizedQuery: string): boolean {
    const raw = String(value ?? '').trim().toLowerCase();
    if (!raw) return false;
    if (raw.includes(query)) return true;
    if (!normalizedQuery) return false;
    const normalized = this.normalizeSearchText(raw);
    return normalized.includes(normalizedQuery);
  }

  private normalizeSearchText(value: string): string {
    return String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9./\s-]+/g, ' ')
      .split(/\s+/)
      .filter((token) => !!token)
      .map((token) => this.normalizeSearchToken(token))
      .join(' ')
      .trim();
  }

  private normalizeSearchToken(token: string): string {
    const cleaned = String(token ?? '').trim();
    if (!cleaned) return '';
    if (cleaned.endsWith('ies') && cleaned.length > 3) {
      return `${cleaned.slice(0, -3)}y`;
    }
    if (cleaned.endsWith('es') && cleaned.length > 4) {
      return cleaned.slice(0, -2);
    }
    if (cleaned.endsWith('s') && cleaned.length > 3) {
      return cleaned.slice(0, -1);
    }
    return cleaned;
  }

  private findBestInheritedVariantRow(
    combo: Record<string, string>,
    previousRows: Array<{
      id: string;
      options: Record<string, string>;
      price: number | null;
      available: number | null;
      image: string | null;
      sku: string;
      barcode: string;
      skuId?: number | string | null;
      shippingWeight?: number | null;
      shippingHeight?: number | null;
      shippingWidth?: number | null;
      shippingLength?: number | null;
      shippingWeightUOM?: number | string | null;
      shippingDimensionUOM?: number | string | null;
    }>
  ): {
    id: string;
    options: Record<string, string>;
    price: number | null;
    available: number | null;
    image: string | null;
    sku: string;
    barcode: string;
    skuId?: number | string | null;
    shippingWeight?: number | null;
    shippingHeight?: number | null;
    shippingWidth?: number | null;
    shippingLength?: number | null;
    shippingWeightUOM?: number | string | null;
    shippingDimensionUOM?: number | string | null;
    } | null {
    let bestMatch: {
      id: string;
      options: Record<string, string>;
      price: number | null;
      available: number | null;
      image: string | null;
      sku: string;
      barcode: string;
      skuId?: number | string | null;
      shippingWeight?: number | null;
      shippingHeight?: number | null;
      shippingWidth?: number | null;
      shippingLength?: number | null;
      shippingWeightUOM?: number | string | null;
      shippingDimensionUOM?: number | string | null;
    } | null = null;
    let bestScore = -1;
    let bestTotalScore = -1;

    for (const row of previousRows) {
      const entries = Object.entries(row?.options || {});
      if (!entries.length) continue;

      let isExactSubsetMatch = true;
      let matchingEntries = 0;
      for (const [key, value] of entries) {
        if (combo[key] === value) {
          matchingEntries += 1;
        } else {
          isExactSubsetMatch = false;
        }
      }

      if (isExactSubsetMatch && entries.length > bestScore) {
        bestMatch = row;
        bestScore = entries.length;
        bestTotalScore = matchingEntries;
        continue;
      }

      if (matchingEntries > 0 && bestScore < 0 && matchingEntries > bestTotalScore) {
        bestMatch = row;
        bestTotalScore = matchingEntries;
      }
    }

    return bestMatch;
  }

  private scheduleRebuild(): void {
    if (this.rebuildTimer) {
      clearTimeout(this.rebuildTimer);
    }
    this.rebuildTimer = setTimeout(() => {
      this.rebuildTimer = null;
      this.rebuildVariantRows();
    }, 0);
  }

  private toServiceAttributes(options: VariantOption[]): any[] {
    return options
      .map((option, index) => {
        const values = this.normalizeValues(option.values);
        const valueIds = option.valueIds || undefined;
        const colorValueMap = option.colorValueMap || undefined;
        let nextValueIds: Record<string, number | string> | undefined = undefined;
        if (valueIds) {
          nextValueIds = {};
          values.forEach((value) => {
            if (valueIds[value] !== undefined) {
              nextValueIds![value] = valueIds[value];
            }
          });
          if (!Object.keys(nextValueIds).length) {
            nextValueIds = undefined;
          }
        }
        return {
          id: option.id ?? null,
          name: this.normalizeName(option.name),
          values,
          position: index + 1,
          valueIds: nextValueIds,
          colorValueMap
        };
      })
      .filter((option) => option.name && option.values.length > 0);
  }

  private syncService(): void {
    this._productService.variantAttributes = this.toServiceAttributes(this.options);
  }

  private pruneUnusedOptionValues(): void {
    if (!Array.isArray(this.options) || !this.options.length) return;

    const usedValuesByOption = new Map<string, Set<string>>();
    this.variantRows.forEach((row) => {
      Object.entries(row?.options || {}).forEach(([optionName, optionValue]) => {
        const normalizedOptionName = this.normalizeName(optionName);
        const normalizedValue = String(optionValue ?? '').trim();
        if (!normalizedOptionName || !normalizedValue) return;
        if (!usedValuesByOption.has(normalizedOptionName)) {
          usedValuesByOption.set(normalizedOptionName, new Set<string>());
        }
        usedValuesByOption.get(normalizedOptionName)?.add(normalizedValue);
      });
    });

    let changed = false;
    this.options = this.options.map((option) => {
      const optionName = this.normalizeName(option?.name);
      if (!optionName) return option;

      const usedValues = usedValuesByOption.get(optionName) || new Set<string>();
      const currentValues = Array.isArray(option?.values) ? option.values : [];
      const nextValues = currentValues.filter((value) => usedValues.has(String(value ?? '').trim()));
      if (nextValues.length === currentValues.length) {
        return option;
      }

      changed = true;
      const nextOption: VariantOption = {
        ...option,
        values: nextValues
      };

      if (option.valueIds) {
        const nextValueIds: Record<string, number | string> = {};
        nextValues.forEach((value) => {
          if (option.valueIds && option.valueIds[value] !== undefined) {
            nextValueIds[value] = option.valueIds[value];
          }
        });
        nextOption.valueIds = Object.keys(nextValueIds).length ? nextValueIds : undefined;
      }
      if (option.colorValueMap) {
        const nextColorValueMap: Record<string, string> = {};
        nextValues.forEach((value) => {
          const mapped = option.colorValueMap?.[value];
          if (mapped) {
            nextColorValueMap[value] = mapped;
          }
        });
        nextOption.colorValueMap = Object.keys(nextColorValueMap).length ? nextColorValueMap : undefined;
      }

      this.updateOptionDefaults(nextOption);
      return nextOption;
    });

    if (changed) {
      this.syncService();
    }
  }

  private syncVariantState(): void {
    this.syncVariantImages();
    this.syncVariantRows();
  }

  private syncVariantImages(): void {
    this._productService.variantImages = this.variantRows
      .filter((row) => !!row.image)
      .map((row) => ({
        id: row.id,
        options: row.options,
        image: row.image
      }));
  }

  private syncVariantRows(): void {
    this._productService.variantRows = this.variantRows;
  }

  private getDefaultVariantPrice(): number | null {
    const raw = this._productService?.pts?.priceA;
    if (raw === null || raw === undefined || raw === '') return 0;
    return this.formatPrice(raw);
  }

  private getDefaultVariantQuantity(): number {
    return 0;
  }

  private getDefaultVariantSku(): string {
    const sku = this._productService?.pts?.sku ?? '';
    return String(sku || '');
  }

  private getDefaultVariantBarcode(): string {
    const barcode = this._productService?.pts?.barcode ?? '';
    return String(barcode || '');
  }

  private formatPrice(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed =
      typeof value === 'number'
        ? value
        : parseFloat(String(value).replace(/,/g, ''));
    if (!Number.isFinite(parsed)) return null;
    return Math.round(parsed * 100) / 100;
  }

  private syncQuickPriceFromVariant(value: string | number | null | undefined): void {
    const normalized = this.formatPrice(value);
    this._productService.pts.priceA = normalized ?? 0;
  }

  private normalizeQuantity(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = typeof value === 'number' ? value : Number(String(value));
    if (!Number.isFinite(parsed)) return null;
    const whole = Math.trunc(parsed);
    return whole < 0 ? 0 : whole;
  }

  private isZeroDisplayValue(value: string): boolean {
    const normalized = value.replace(/[^0-9.-]/g, '');
    if (!normalized) return false;
    const numericValue = Number(normalized);
    return !isNaN(numericValue) && numericValue === 0;
  }


  private validateOptionName(index: number): void {
    const option = this.options[index];
    if (!option) return;
    const normalized = this.normalizeName(option.name);
    if (!normalized) {
      this.optionNameErrors.delete(index);
      return;
    }
    const duplicateIndex = this.findDuplicateOptionIndex(normalized, index);
    if (duplicateIndex >= 0) {
      this.optionNameErrors.add(index);
    } else {
      this.optionNameErrors.delete(index);
    }
  }

  private validateAllOptionNames(): void {
    this.options.forEach((_, index) => this.validateOptionName(index));
  }

  private validateVariantCodes(): boolean {
    this.variantSkuRequired.clear();
    this.variantSkuDuplicate.clear();
    this.variantBarcodeRequired.clear();
    this.variantBarcodeDuplicate.clear();

    if (!this.variantRows.length) {
      return true;
    }

    for (const row of this.variantRows) {
      const sku = this.normalizeCodeValue(row.sku);
      if (!sku) {
        this.variantSkuRequired.add(row.id);
      }

      const barcode = this.normalizeCodeValue(row.barcode);
      if (!barcode) {
        this.variantBarcodeRequired.add(row.id);
      }
    }

    return (
      !this.variantSkuRequired.size &&
      !this.variantSkuDuplicate.size &&
      !this.variantBarcodeRequired.size &&
      !this.variantBarcodeDuplicate.size
    );
  }

  private buildVariantSuffix(row: { options?: Record<string, string> }): string {
    const values = Object.values(row?.options || {});
    const parts = values
      .map((value) => this.normalizeCodePart(value))
      .filter((part) => part.length > 0);
    return parts.join('-');
  }

  private normalizeCodePart(value: string): string {
    return String(value || '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toUpperCase();
  }

  private normalizeCodeValue(value: string | null | undefined): string {
    return String(value ?? '').trim();
  }

  private toCodeKey(value: string): string {
    return String(value || '').trim().toLowerCase();
  }

  private ensureUniqueCode(candidate: string, used: Set<string>): string {
    let base = String(candidate || '').trim();
    if (!base) {
      base = 'VAR';
    }
    let key = this.toCodeKey(base);
    if (!used.has(key)) {
      used.add(key);
      return base;
    }
    let counter = 2;
    let next = `${base}-${counter}`;
    while (used.has(this.toCodeKey(next))) {
      counter += 1;
      next = `${base}-${counter}`;
    }
    used.add(this.toCodeKey(next));
    return next;
  }

  private buildDefaultVariantCode(
    seed: string,
    row: { options?: Record<string, string> },
    used: Set<string>,
    fallbackPrefix: string,
    preferNumeric = false
  ): string {
    const base = this.normalizeCodeValue(seed);
    if (base) {
      return base;
    }
    if (preferNumeric) {
      return this.generateUniqueNumericCode(used);
    }
    return this.ensureUniqueCode(fallbackPrefix, used);
  }

  private isNumericCode(value: string): boolean {
    return !!value && /^[0-9]+$/.test(value);
  }

  private generateUniqueNumericCode(used: Set<string>): string {
    let candidate = GlobalService.generateSKU();
    let key = this.toCodeKey(candidate);
    while (used.has(key)) {
      candidate = GlobalService.generateSKU();
      key = this.toCodeKey(candidate);
    }
    used.add(key);
    return candidate;
  }

  private getSelectedValueSet(values: string[]): Set<string> {
    return new Set(
      (values || [])
        .map((v) => String(v).trim().toLowerCase())
        .filter((v) => v.length > 0)
    );
  }
}
